import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../contexts/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import Tooltip from '../components/Tooltip';
import ImagemProduto from '../components/ImagemProduto';
import CampanhaTracker from '../components/CampanhaTracker';
import ReplicarPrecosModal, { destinoDiverge } from '../components/ReplicarPrecosModal';
import { maskMoeda, parseMoeda, parseMoedaOuNull, numeroParaMoeda } from '../helpers/masks';
import { lerPrimeiraColuna } from '../helpers/planilha';
import { linkEstudio } from '../utils/estudio';

// Uma linha do PUT /produtos/campanha/:id/lote a partir do estado da tela (s = { d, x, c, q, a, z, pc })
const linhaDiretoria = (pid, s) => ({
  id: pid,
  preco_diretoria: parseMoedaOuNull(s.d[pid]),
  aprovado: s.a[pid] ? true : false,
  comissao: parseMoedaOuNull(s.c[pid]),
  preco_12x: parseMoedaOuNull(s.x[pid]),
  qtd_ofertada: parseMoedaOuNull(s.q[pid]),
  preco_prazo: parseMoedaOuNull(s.z[pid]),
  perc_prazo: parseMoedaOuNull(s.pc[pid])
});

export default function CampanhaProdutos() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, temPermissao } = useAuth();

  const [campanha, setCampanha] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [sugestoes, setSugestoes] = useState([]);
  const [estoque, setEstoque] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [filiaisExcluidas, setFiliaisExcluidas] = useState([]);
  const [expandido, setExpandido] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [busca, setBusca] = useState('');
  // Lista visível "congelada": Set de ids que casavam no filtro quando ele foi aplicado.
  // Só é recalculado ao mudar filtro/busca ou ao recarregar os produtos — nunca durante a digitação.
  const [idsFiltro, setIdsFiltro] = useState(null);
  // Qual campo de valor está sendo digitado agora (id:campo) — deixa o auto-save mais paciente.
  const [campoEmEdicao, setCampoEmEdicao] = useState(null);
  const campoEmEdicaoRef = useRef(null);

  // Replicar preço nas outras listas (mesmo produto em campanha aberta do mesmo período)
  const [replicar, setReplicar] = useState(null); // { modo: 'um' | 'lote', itens }
  const [replicando, setReplicando] = useState(false);
  const [replicarSempre, setReplicarSempre] = useState(false); // vale até sair da tela
  const replicarSempreRef = useRef(false);
  const filaReplicarRef = useRef([]);     // avisos que chegaram com outro aberto
  const replicarAbertoRef = useRef(null);
  const linhaEmEdicaoRef = useRef(null);  // { id, antes } — valores ao entrar nos campos de preço da linha

  // Modal busca
  const [showBusca, setShowBusca] = useState(false);
  const [buscaCodpro, setBuscaCodpro] = useState('');
  const [buscaClasses, setBuscaClasses] = useState([]);
  const [classeInput, setClasseInput] = useState('');
  const [classeDropOpen, setClasseDropOpen] = useState(false);
  const [buscaDescricao, setBuscaDescricao] = useState('');
  const [buscaDisponibilidades, setBuscaDisponibilidades] = useState([]);
  const [dispInput, setDispInput] = useState('');
  const [dispDropOpen, setDispDropOpen] = useState(false);
  const [disponibilidades, setDisponibilidades] = useState([]);
  const [classes, setClasses] = useState([]);
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  // Lista de staging — produtos a adicionar (acumula código rápido + seleção da busca), salva tudo no fim
  const [paraAdicionar, setParaAdicionar] = useState([]);
  const [codigoRapido, setCodigoRapido] = useState('');
  const [buscandoCodigo, setBuscandoCodigo] = useState(false);
  const [importandoPlanilha, setImportandoPlanilha] = useState(false);
  const inputPlanilhaRef = useRef(null);
  const [buscandoProdutos, setBuscandoProdutos] = useState(false);
  const [buscou, setBuscou] = useState(false); // já rodou uma busca (pra distinguir "0 resultados" de "ainda não buscou")
  const [buscaComEstoque, setBuscaComEstoque] = useState(false); // filtro: só produtos com estoque disponível > 0

  // Modal lote
  const [showLote, setShowLote] = useState(false);
  const [descontoPerc, setDescontoPerc] = useState('');
  const [classeLote, setClasseLote] = useState('');
  const [terminacaoLote, setTerminacaoLote] = useState(''); // centavos p/ arredondar (ex: 90, 99); vazio = sem arredondar
  const [previewLote, setPreviewLote] = useState([]);

  // Modal remover em lote (limpeza por disponibilidade / sem estoque)
  const [showRemoverLote, setShowRemoverLote] = useState(false);
  const [remDisponibs, setRemDisponibs] = useState([]);       // disponibilidades marcadas p/ remover
  const [remClasses, setRemClasses] = useState([]);           // classes p/ restringir a remoção (opcional)
  const [remSemEstoque, setRemSemEstoque] = useState(false);  // marcar tb os sem estoque disponível
  const [remExcluidos, setRemExcluidos] = useState([]);       // ids que o usuário decidiu MANTER (tirar da remoção)
  const [removendoLote, setRemovendoLote] = useState(false);

  // Flags de carregando p/ ações que faltavam feedback visual
  const [salvandoLista, setSalvandoLista] = useState(false);
  const [simulandoLote, setSimulandoLote] = useState(false);
  const [aplicandoLote, setAplicandoLote] = useState(false);

  // Auto-save: salva sozinho após parar de mexer. status: idle | saving | saved | erro
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const savedSigRef = useRef('');        // assinatura do último estado salvo/carregado (diretoria)
  const savedLinhasRef = useRef({});     // id -> linha (JSON) como está no banco; o auto-save manda só as que mudaram
  const estadoRef = useRef(null);        // estado atual da diretoria, lido fora do render (timeouts/eventos)
  const savedSugSigRef = useRef('{}');   // idem p/ sugestões do gerente
  const autoSaveTimer = useRef(null);
  const autoSaveSugTimer = useRef(null);

  // Menu de ações (dropdown)
  const [menuAcoesOpen, setMenuAcoesOpen] = useState(false);

  // Modal sugestões por filial
  const [showSugestoes, setShowSugestoes] = useState(null); // produtoId

  // Modal histórico
  const [showHistorico, setShowHistorico] = useState(null); // produtoId
  const [historico, setHistorico] = useState([]);

  // Modal estoque por filial
  const [showEstoqueFilial, setShowEstoqueFilial] = useState(null); // codpro
  const [estoquePorFilial, setEstoquePorFilial] = useState(null); // { estoque: [], pedidos: [] }

  // Vendas no ano anterior — batch para todos os produtos
  const [vendasAnoAnterior, setVendasAnoAnterior] = useState(null); // { ano_anterior, periodo_referencia, por_codpro }
  const [showVendasFilial, setShowVendasFilial] = useState(null); // { codpro, escopo: 'periodo' | 'ano' }

  // Edição local
  const [precosDiretoria, setPrecosDiretoria] = useState({});
  const [precos12x, setPrecos12x] = useState({});
  const [comissoes, setComissoes] = useState({});
  const [qtdOfertadas, setQtdOfertadas] = useState({});
  const [aprovados, setAprovados] = useState({});
  // Precificação a prazo: preço a prazo digitado + % próprio do produto ('' = usa o da campanha)
  const [precosPrazo, setPrecosPrazo] = useState({});
  const [percsPrazo, setPercsPrazo] = useState({});
  const [taxaPrazoSistema, setTaxaPrazoSistema] = useState(null); // taxa da forma de pagamento a prazo (ERP)
  const autoPrazoTentadosRef = useRef(new Set()); // ids já tentados no preenchimento automático

  // Salvando
  const [salvando, setSalvando] = useState(false);

  // Sugestões locais (gerente)
  const [sugestoesLocal, setSugestoesLocal] = useState({});

  // Ordenação
  const [ordenacao, setOrdenacao] = useState({ campo: null, direcao: 'asc' });

  // Plaquinhas loading
  const [gerandoPlaquinhas, setGerandoPlaquinhas] = useState(false);
  const [qtdPlaquinhas, setQtdPlaquinhas] = useState(0);

  // ERP
  const [enviandoERP, setEnviandoERP] = useState(false);
  const [erpStatus, setErpStatus] = useState({}); // codpro -> { avista:{ofertada,vendida,...}, doze:{...} }
  const [sincronizandoUm, setSincronizandoUm] = useState(null); // produtoId em sincronização individual

  const carregar = useCallback(async () => {
    try {
      const [camp, prods, sugs, fil, excl] = await Promise.all([
        axios.get(`${API}/campanhas/${id}`),
        axios.get(`${API}/produtos/campanha/${id}`),
        axios.get(`${API}/sugestoes/campanha/${id}`),
        axios.get(`${API}/filiais`),
        axios.get(`${API}/campanhas/${id}/filiais-excluidas`)
      ]);
      setCampanha(camp.data);
      setProdutos(prods.data);
      setSugestoes(sugs.data);
      setFiliais(fil.data);
      setFiliaisExcluidas(excl.data.map(e => e.filial_codigo));

      const pDir = {}, apr = {}, com = {}, p12 = {}, qof = {}, pz = {}, pc = {};
      prods.data.forEach(p => {
        pDir[p.id] = numeroParaMoeda(p.preco_diretoria);
        pz[p.id] = numeroParaMoeda(p.preco_prazo);
        pc[p.id] = numeroParaMoeda(p.perc_prazo);
        apr[p.id] = p.aprovado ? true : false;
        com[p.id] = numeroParaMoeda(p.comissao);
        p12[p.id] = numeroParaMoeda(p.preco_12x);
        qof[p.id] = numeroParaMoeda(p.qtd_ofertada);
      });
      setPrecosDiretoria(pDir);
      setAprovados(apr);
      setComissoes(com);
      setPrecos12x(p12);
      setQtdOfertadas(qof);
      setPrecosPrazo(pz);
      setPercsPrazo(pc);
      // Marca o estado recém-carregado como "já salvo" (evita auto-save no load/reload)
      const carregado = { d: pDir, x: p12, c: com, q: qof, a: apr, z: pz, pc };
      savedSigRef.current = JSON.stringify(carregado);
      savedLinhasRef.current = {};
      prods.data.forEach(p => { savedLinhasRef.current[p.id] = JSON.stringify(linhaDiretoria(p.id, carregado)); });
      setAutoSaveStatus('idle');

      // Status no ERP (ofertada/vendida por produto) — só se a campanha já tem oferta
      if (camp.data?.erp_oferta_oid) {
        axios.get(`${API}/erp/campanha/${id}/produtos-erp`)
          .then(r => setErpStatus(r.data?.produtos || {}))
          .catch(() => setErpStatus({}));
      } else {
        setErpStatus({});
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  const carregarEstoque = async () => {
    try {
      const res = await axios.get(`${API}/estoque/campanha/${id}`);
      setEstoque(res.data);
      setUltimaAtualizacao(new Date());
    } catch (err) {
      Swal.fire('Erro', 'Erro ao carregar estoque', 'error');
    }
  };

  useEffect(() => { if (produtos.length > 0) carregarEstoque(); }, [produtos.length]);

  // Carrega vendas do ano anterior em batch (todos os produtos de uma vez)
  useEffect(() => {
    if (produtos.length > 0 && !vendasAnoAnterior) {
      axios.get(`${API}/produtos/campanha/${id}/vendas-anterior-todos`)
        .then(res => setVendasAnoAnterior(res.data))
        .catch(() => {});
    }
  }, [produtos.length]);

  useEffect(() => {
    axios.get(`${API}/produtos/classes`).then(res => setClasses(res.data)).catch(() => {});
    axios.get(`${API}/produtos/disponibilidades`).then(res => setDisponibilidades(res.data)).catch(() => {});
  }, []);

  // Helpers
  // Escopo por filial: 'especificas' = só as filiais participantes; senão todas. Sempre tira as excluídas.
  const escopoEspecificas = campanha?.escopo === 'especificas';
  const filiaisParticipantes = campanha?.filiais_participantes || [];
  const filiaisAtivas = filiais.filter(f => {
    const cod = parseInt(f.codigo);
    if (escopoEspecificas && !filiaisParticipantes.includes(cod)) return false;
    return !filiaisExcluidas.includes(cod);
  });
  // Coleta de sugestão dos gerentes é opcional (BIT na campanha; default ligado)
  const coletaSugestoes = !(campanha?.coletar_sugestoes_gerente === false || campanha?.coletar_sugestoes_gerente === 0);
  // Gerente de loja = perfil "Gerente de Loja" (permissão sugestoes_preencher mas SEM preco_diretoria)
  const isGerente = user?.perfil_nome === 'Gerente de Loja' || (temPermissao('sugestoes_preencher') && !temPermissao('preco_diretoria') && !temPermissao('campanhas_editar'));
  const podeDiretoria = temPermissao('preco_diretoria');
  const veDiretoria = podeDiretoria || !isGerente; // marketing/gestor vê colunas (somente leitura)
  // Mostra o bloco de sugestões (GERENTES / MINHA SUGESTÃO) só se a campanha coleta
  const veSugestoes = coletaSugestoes;
  const tem12x = campanha?.tem_preco_12x;
  // Tipo de precificação: 'padrao' = digita o Preço; 'prazo' = digita o preço a prazo e o
  // Preço sai de prazo × (1 − %). O que vai pro ERP/plaquinha continua sendo o Preço.
  const modoPrazo = campanha?.tipo_precificacao === 'prazo';
  const percCampanha = campanha?.perc_prazo != null ? parseFloat(campanha.perc_prazo) : null;
  // Faixas de arredondamento dos centavos do Preço calculado: [{de, ate, para}] em centavos
  const faixasArred = (() => { try { const f = JSON.parse(campanha?.arredondamento || '[]'); return Array.isArray(f) ? f : []; } catch { return []; } })();

  const getEstoque = (codpro) => estoque.find(e => e.codpro === codpro?.trim());

  const calcMedia = (produtoId) => {
    const sugs = sugestoes.filter(s => s.campanha_produto_id === produtoId && !filiaisExcluidas.includes(s.filial_codigo));
    if (!sugs.length) return { preco: null, mkp: null, count: 0 };
    const preco = sugs.reduce((a, s) => a + parseFloat(s.preco_sugerido || 0), 0) / sugs.length;
    const mkp = sugs.reduce((a, s) => a + parseFloat(s.mkp_sugerido || 0), 0) / sugs.length;
    return { preco, mkp, count: sugs.length };
  };

  // Formatação e leitura numérica dos campos mascarados (o state guarda string BR "1.234,56").
  // Precisam vir ANTES de getAvisos/getValidacoesErp e do cálculo da lista filtrada: essas
  // funções chamam numDir() durante o render e, definidas depois, davam
  // "Cannot access 'numDir' before initialization" nos filtros Críticos / Avisos / Pendência ERP.
  const fmt = (v) => v != null ? parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
  const fmtMkp = (v) => v != null ? (parseFloat(v) * 100).toFixed(1) + '%' : '—';
  // Número "limpo" — 2 casas mas sem zeros à toa (2.04, 5, 4.04)
  const round2 = (v) => { const n = Math.round((parseFloat(v) || 0) * 100) / 100; return Number.isInteger(n) ? String(n) : n.toFixed(2); };
  const numDir = (id) => parseMoeda(precosDiretoria[id]);
  const num12x = (id) => parseMoeda(precos12x[id]);
  const numPrazo = (id) => parseMoeda(precosPrazo[id]);
  const temPercProprio = (id) => percsPrazo[id] !== undefined && percsPrazo[id] !== null && percsPrazo[id] !== '';
  // % efetivo do produto: o próprio (se preenchido, inclusive 0) ou o padrão da campanha
  const percEfetivo = (id) => temPercProprio(id) ? parseMoeda(percsPrazo[id]) : (percCampanha ?? 0);
  // Mesma conta do backend (PUT /produtos/campanha/:id/lote)
  const precoDoPrazo = (prazo, perc) => Math.round(prazo * (1 - (perc || 0) / 100) * 100) / 100;
  // Arredonda os centavos pela faixa que casar (dentro do mesmo real). Fora das faixas, fica igual.
  // Ex. faixas [{1,49→50},{51,99→90}]: 92,04 → 92,50 · 92,63 → 92,90 · 92,00 → 92,00
  const arredondarPreco = (valor, faixas = faixasArred) => {
    if (!(valor > 0) || !faixas.length) return valor;
    const reais = Math.floor(valor + 1e-9);
    const cent = Math.round((valor - reais) * 100);
    const f = faixas.find(x => cent >= x.de && cent <= x.ate);
    return f ? Math.round((reais + f.para / 100) * 100) / 100 : valor;
  };
  const precoCalculado = (prazo, perc, faixas = faixasArred) => arredondarPreco(precoDoPrazo(prazo, perc), faixas);
  // Preço "manual" = a diretoria sobrescreveu: difere do que o prazo + % + arredondamento dariam
  const ehPrecoManual = (id, perc = percEfetivo(id), faixas = faixasArred) => {
    if (!(numPrazo(id) > 0) || !(numDir(id) > 0)) return false;
    return Math.abs(numDir(id) - precoCalculado(numPrazo(id), perc, faixas)) > 0.004;
  };
  const numCom = (id) => parseMoeda(comissoes[id]);
  const numQtd = (id) => parseMoedaOuNull(qtdOfertadas[id]); // null quando vazio (fallback pro estoque)

  // Status principal — reflete o estado do fluxo (NOVO → SUGESTÕES → DEFINIDO → APROVADO)
  const getStatusBadge = (prod, est) => {
    const precoDir = precosDiretoria[prod.id];
    const sugs = sugestoes.filter(s => s.campanha_produto_id === prod.id);

    if (aprovados[prod.id]) return { label: 'APROVADO', cls: 'status-ok', icon: 'bi-check-circle-fill' };
    if (precoDir) return { label: 'PREÇO DEFINIDO', cls: 'status-preco-definido', icon: 'bi-currency-dollar' };
    if (sugs.length > 0) return { label: 'COM SUGESTÕES', cls: 'status-sugestoes', icon: 'bi-chat-dots' };
    return { label: 'NOVO', cls: 'status-sem-sugestao', icon: 'bi-plus-circle' };
  };

  // Avisos — múltiplos badges/ícones que aparecem ao lado do status principal.
  // Cada aviso tem severidade (critico/aviso/info) e tooltip explicando.
  const getAvisos = (prod, est) => {
    const numLojas = filiais.length || 8;
    const estoqueDisp = est?.estoque_disp || 0;
    const aChegar = est?.qtd_a_chegar || 0;
    const custo = parseFloat(prod.custo) || 0;
    const precoSistema = parseFloat(prod.preco_sistema) || 0;
    const precoDir = numDir(prod.id);
    const codpro = prod.codpro?.trim();
    const vendasProd = vendasAnoAnterior?.por_codpro?.[codpro];
    const vendaPeriodoAnt = vendasProd ? vendasProd.mesmo_periodo.reduce((a, v) => a + v.qtd, 0) : 0;
    const vendaAnoAnt = vendasProd ? vendasProd.ano_inteiro.qtd : 0;

    const avisos = [];

    // 🔴 MKP negativo (preço diretoria < custo)
    if (precoDir > 0 && custo > 0 && precoDir < custo) {
      avisos.push({
        chave: 'mkp_negativo',
        icon: 'bi-exclamation-octagon-fill',
        cor: '#c82333',
        msg: `MKP negativo — preço diretoria R$ ${precoDir.toFixed(2)} é menor que o custo R$ ${custo.toFixed(2)}`,
      });
    }

    // 🔴 Vai faltar — estoque + a chegar < venda no mesmo período do ano anterior
    if (vendasProd && vendaPeriodoAnt > 0 && (estoqueDisp + aChegar) < vendaPeriodoAnt) {
      const falta = vendaPeriodoAnt - (estoqueDisp + aChegar);
      avisos.push({
        chave: 'vai_faltar',
        icon: 'bi-cart-x-fill',
        cor: '#c82333',
        msg: `Estoque ${estoqueDisp.toFixed(0)} + a chegar ${aChegar.toFixed(0)} = ${(estoqueDisp + aChegar).toFixed(0)} un. Em ${vendasAnoAnterior?.ano_anterior} vendeu ${vendaPeriodoAnt.toFixed(0)} un no mesmo período (faltam ${falta.toFixed(0)} un)`,
      });
    }

    // 🟠 Estoque crítico (< 1 por loja em média) — só aparece se não tem "vai faltar"
    if (!avisos.find(a => a.chave === 'vai_faltar') && estoqueDisp < numLojas && estoqueDisp >= 0) {
      avisos.push({
        chave: 'estoque_critico',
        icon: 'bi-exclamation-triangle-fill',
        cor: '#dc3545',
        msg: `Estoque crítico — ${estoqueDisp.toFixed(0)} un para ${numLojas} lojas (média menor que 1 un por loja)`,
      });
    }

    // 🟠 MKP baixo (< 10%, mas não negativo)
    if (precoDir > 0 && custo > 0 && precoDir >= custo) {
      const mkp = (precoDir - custo) / custo;
      if (mkp < 0.10) {
        avisos.push({
          chave: 'mkp_baixo',
          icon: 'bi-graph-down-arrow',
          cor: '#fd7e14',
          msg: `MKP da diretoria é ${(mkp * 100).toFixed(1)}% (abaixo de 10%)`,
        });
      }
    }

    // 🟠 Preço acima do sistema
    if (precoDir > 0 && precoSistema > 0 && precoDir > precoSistema) {
      avisos.push({
        chave: 'acima_sistema',
        icon: 'bi-arrow-up-circle-fill',
        cor: '#fd7e14',
        msg: `Preço diretoria R$ ${precoDir.toFixed(2)} é maior que o preço do sistema R$ ${precoSistema.toFixed(2)}`,
      });
    }

    // 🟡 Sem desconto real (preço diretoria == preço sistema)
    if (precoDir > 0 && precoSistema > 0 && Math.abs(precoDir - precoSistema) < 0.01) {
      avisos.push({
        chave: 'sem_desconto',
        icon: 'bi-tag',
        cor: '#ffc107',
        msg: `Preço da diretoria é igual ao do sistema (R$ ${precoSistema.toFixed(2)})`,
      });
    }

    // 🟡 Sem histórico no ano anterior
    if (vendasAnoAnterior && vendasProd && vendaPeriodoAnt === 0 && vendaAnoAnt === 0) {
      avisos.push({
        chave: 'sem_historico',
        icon: 'bi-question-circle',
        cor: '#6c757d',
        msg: `Sem vendas registradas em ${vendasAnoAnterior.ano_anterior}`,
      });
    }

    // 🟢 Estoque com folga (> 3× venda do mesmo período anterior)
    if (vendasProd && vendaPeriodoAnt > 0 && (estoqueDisp + aChegar) > vendaPeriodoAnt * 3) {
      avisos.push({
        chave: 'estoque_sobra',
        icon: 'bi-rocket-takeoff',
        cor: '#198754',
        // Separa o que já está nas lojas do que ainda vai chegar — "Estoque 53" sozinho confundia quem via 13 no ERP
        msg: `Estoque de sobra: ${aChegar > 0
          ? `${(estoqueDisp + aChegar).toFixed(0)} un (${estoqueDisp.toFixed(0)} nas lojas + ${aChegar.toFixed(0)} a caminho)`
          : `${estoqueDisp.toFixed(0)} un nas lojas`} — mais de 3× o que vendeu no mesmo período de ${vendasAnoAnterior.ano_anterior} (${vendaPeriodoAnt.toFixed(0)} un). Não deve faltar.`,
      });
    }

    return avisos;
  };

  // Validações do ERP (pré-flight da sincronização) — cientes do cenário:
  //  • "criar" (oferta ainda não existe): valida preço à vista < sistema e preço 12x.
  //  • "atualizar" (oferta já existe): idem + a quantidade não pode ficar abaixo do já vendido.
  // Retorna { ok, cenario, itens: [{ nivel: 'bloqueio'|'ok', msg }] }.
  const getValidacoesErp = (prod, est) => {
    const ofertaCriada = !!campanha?.erp_oferta_oid;
    const cenario = ofertaCriada ? 'atualizar' : 'criar';
    const precoDir = numDir(prod.id);
    const precoSist = parseFloat(prod.preco_sistema) || 0;
    const preco12 = num12x(prod.id);
    const st = erpStatus[prod.codpro?.trim()] || {};
    const vm = parseFloat(est?.venda_minima) || 0;
    const vendMax = Math.max(st.avista?.vendida || 0, st.doze?.vendida || 0);
    const qtdInt = numQtd(prod.id) ?? (est?.estoque_disp || 0);
    const itens = [];

    // Preço à vista: obrigatório e tem que ser MENOR que o preço do sistema (a oferta é desconto)
    if (!(precoDir > 0)) itens.push({ nivel: 'bloqueio', msg: 'Sem preço à vista definido' });
    else if (precoSist > 0 && precoDir >= precoSist) itens.push({ nivel: 'bloqueio', msg: `Preço à vista R$ ${precoDir.toFixed(2)} não é menor que o do sistema R$ ${precoSist.toFixed(2)} — o ERP recusa (a oferta precisa ser um desconto)` });

    // Preço 12x: obrigatório quando a campanha tem 12x
    if (tem12x && !(preco12 > 0)) itens.push({ nivel: 'bloqueio', msg: 'Sem preço 12x definido' });

    // Só na atualização (oferta já criada): não pode ofertar menos do que já foi vendido
    if (ofertaCriada && vendMax > 0 && qtdInt < vendMax) {
      itens.push({ nivel: 'bloqueio', msg: `Quantidade ${round2(qtdInt)} é menor que o já vendido na oferta (${round2(vendMax)}) — o ERP não deixa reduzir abaixo do vendido. Informe pelo menos ${round2(vendMax)}.` });
    }

    // Informações de apoio (não bloqueiam) — venda mínima (múltiplo) e quantidade já vendida
    if (vm > 0) itens.push({ nivel: 'info', msg: `Venda mínima ${round2(vm)} — a quantidade ofertada tem que ser múltiplo de ${round2(vm)}` });
    if (ofertaCriada && vendMax > 0) itens.push({ nivel: 'info', msg: `Já vendido nesta oferta: ${round2(vendMax)} (a quantidade não pode ser menor)` });

    const bloqueios = itens.filter(i => i.nivel === 'bloqueio');
    if (bloqueios.length === 0) itens.unshift({ nivel: 'ok', msg: ofertaCriada ? 'Passa nas validações — será atualizado no ERP' : 'Passa nas validações — entra na oferta ao criar' });
    return { ok: bloqueios.length === 0, cenario, itens, bloqueios };
  };

  // Filtro
  const toggleOrdenacao = (campo) => {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc'
    }));
  };

  const ThSort = ({ campo, children, style = {} }) => (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textAlign: 'center', verticalAlign: 'middle', ...style }} onClick={() => toggleOrdenacao(campo)}>
      {children}
      <i className={`bi bi-chevron-${ordenacao.campo === campo ? (ordenacao.direcao === 'asc' ? 'up' : 'down') : 'expand'} ms-1`}
        style={{ fontSize: '0.65rem', opacity: ordenacao.campo === campo ? 1 : 0.3 }}></i>
    </th>
  );

  // Na tela mostra a descrição longa do ERP (COMPLEMENTOPRODUTO); se não houver, a curta.
  // A curta continua sendo a gravada — é ela que sai na plaquinha e vai pro ERP.
  const descTela = (p) => (p?.descricao_longa || p?.descricao || '');

  const casaNoFiltro = (p) => {
    // Filtro dinâmico por texto (código ou descrição)
    if (busca.trim() && !`${p.codpro || ''} ${p.descricao || ''} ${p.descricao_longa || ''}`.toLowerCase().includes(busca.trim().toLowerCase())) return false;
    const est = getEstoque(p.codpro);
    if (filtroStatus === 'todos') return true;
    if (filtroStatus === 'aprovados') return aprovados[p.id];
    if (filtroStatus === 'pendente_aprovacao') return !aprovados[p.id];
    if (filtroStatus === 'preco_definido') return precosDiretoria[p.id] && !aprovados[p.id];
    if (filtroStatus === 'sem_preco') return !precosDiretoria[p.id];
    if (filtroStatus === 'critico') {
      const avs = getAvisos(p, est);
      return avs.some(a => ['mkp_negativo', 'vai_faltar', 'estoque_critico'].includes(a.chave));
    }
    if (filtroStatus === 'avisos') {
      return getAvisos(p, est).length > 0;
    }
    if (filtroStatus === 'erp_pendente') {
      return veDiretoria && !getValidacoesErp(p, est).ok;
    }
    return true;
  };

  // A linha NÃO some da lista enquanto o usuário digita. Antes, com o filtro "Sem preço",
  // o primeiro dígito do preço (a máscara lê centavos: "1" -> 0,01) já tirava o produto do
  // filtro: o input era desmontado, o foco se perdia e o auto-save gravava o 0,01. Agora a
  // lista é um snapshot, refeito só quando o filtro/busca muda, os produtos recarregam ou o
  // usuário clica em "atualizar lista".
  const reaplicarFiltro = () => setIdsFiltro(new Set(produtos.filter(casaNoFiltro).map(p => p.id)));
  // Deps de propósito só do filtro/lista — precosDiretoria & cia. NÃO entram aqui (é o que congela a lista).
  useEffect(() => {
    reaplicarFiltro();
  }, [produtos, estoque, filtroStatus, busca, veDiretoria]); // eslint-disable-line

  const produtosFiltrados = produtos.filter(p => idsFiltro ? idsFiltro.has(p.id) : casaNoFiltro(p)).sort((a, b) => {
    if (!ordenacao.campo) return 0;
    const dir = ordenacao.direcao === 'asc' ? 1 : -1;
    const campo = ordenacao.campo;

    let va, vb;
    if (campo === 'codpro') { va = a.codpro; vb = b.codpro; }
    else if (campo === 'descricao') { va = descTela(a); vb = descTela(b); }
    else if (campo === 'disponibilidade') { va = a.disponibilidade || ''; vb = b.disponibilidade || ''; }
    else if (campo === 'custo') { va = parseFloat(a.custo) || 0; vb = parseFloat(b.custo) || 0; }
    else if (campo === 'preco_sistema') { va = parseFloat(a.preco_sistema) || 0; vb = parseFloat(b.preco_sistema) || 0; }
    else if (campo === 'mkp_sistema') { va = parseFloat(a.mkp_sistema) || 0; vb = parseFloat(b.mkp_sistema) || 0; }
    else if (campo === 'a_chegar') { va = getEstoque(a.codpro)?.qtd_a_chegar || 0; vb = getEstoque(b.codpro)?.qtd_a_chegar || 0; }
    else if (campo === 'media') { va = calcMedia(a.id).preco || 0; vb = calcMedia(b.id).preco || 0; }
    else if (campo === 'preco_diretoria') { va = numDir(a.id); vb = numDir(b.id); }
    else if (campo === 'preco_prazo') { va = numPrazo(a.id); vb = numPrazo(b.id); }
    else if (campo === 'comissao') { va = numCom(a.id); vb = numCom(b.id); }
    else if (campo === 'status') { va = getStatusBadge(a, getEstoque(a.codpro)).label; vb = getStatusBadge(b, getEstoque(b.codpro)).label; }
    else return 0;

    if (typeof va === 'string') return va.localeCompare(vb) * dir;
    return (va - vb) * dir;
  });

  // Notificação discreta (toast que some sozinho)
  const toast = (title, icon = 'info') => Swal.fire({ toast: true, position: 'top-end', icon, title, showConfirmButton: false, timer: 3000, timerProgressBar: true });

  // Ao sair do campo Qtd ofertada: se não for múltiplo da venda mínima, arredonda pra CIMA
  // ao múltiplo mais próximo e avisa discretamente (ex.: vm 2.02, digitou 4 → 4.04).
  const ajustarQtdMultiplo = (prodId, codpro) => {
    const val = parseMoeda(qtdOfertadas[prodId]);
    if (!(val > 0)) return;
    const vm = parseFloat(getEstoque(codpro)?.venda_minima) || 0;
    if (!(vm > 0)) return;
    const passos = Math.round(val / vm);
    const jaMultiplo = Math.abs(passos * vm - val) < 1e-6;
    if (jaMultiplo) return;
    const novo = Math.round(Math.ceil(val / vm - 1e-9) * vm * 100) / 100;
    setQtdOfertadas(prev => ({ ...prev, [prodId]: numeroParaMoeda(novo) }));
    toast(`Qtd ajustada para ${round2(novo)} (múltiplo da venda mínima ${round2(vm)})`);
  };

  // Preço 12x automático quando altera preço diretoria (valor já vem mascarado "1.234,56")
  const handlePrecoDir = (prodId, valor) => {
    setPrecosDiretoria(prev => ({ ...prev, [prodId]: valor }));
    if (tem12x && valor) {
      const parcela = parseMoeda(valor) / 10;
      setPrecos12x(prev => ({ ...prev, [prodId]: numeroParaMoeda(parcela * 12) }));
    }
  };

  // Precificação a prazo: recalcula Preço (e 12x, se a campanha tem) a partir do prazo e do %.
  // Prazo vazio limpa o Preço — nesse modo ele só existe derivado do prazo.
  const recalcularDoPrazo = (prodId, prazo, perc, faixas = faixasArred) => {
    if (!(prazo > 0)) {
      setPrecosDiretoria(prev => ({ ...prev, [prodId]: '' }));
      if (tem12x) setPrecos12x(prev => ({ ...prev, [prodId]: '' }));
      return;
    }
    const preco = precoCalculado(prazo, perc, faixas);
    setPrecosDiretoria(prev => ({ ...prev, [prodId]: numeroParaMoeda(preco) }));
    if (tem12x) setPrecos12x(prev => ({ ...prev, [prodId]: numeroParaMoeda(Math.round(preco * 1.2 * 100) / 100) }));
  };
  const handlePrazo = (prodId, valor) => {
    setPrecosPrazo(prev => ({ ...prev, [prodId]: valor }));
    recalcularDoPrazo(prodId, parseMoeda(valor), percEfetivo(prodId));
  };
  // % do produto: máscara de 2 casas ("1000" → 10,00), teto 99,99. Vazio = volta pro % da campanha.
  const handlePercProd = (prodId, valor) => {
    if (valor && parseMoeda(valor) >= 100) valor = '99,99';
    setPercsPrazo(prev => ({ ...prev, [prodId]: valor }));
    recalcularDoPrazo(prodId, numPrazo(prodId), valor !== '' ? parseMoeda(valor) : (percCampanha ?? 0));
  };

  // Preenche o 12x de TODOS os produtos com o padrão (à vista x 1,2 = /10 x12).
  // Só nos que já têm preço à vista definido. O auto-save salva sozinho depois.
  const aplicar12xPadrao = () => {
    const novo = {};
    let n = 0;
    produtos.forEach(p => {
      const av = parseMoeda(precosDiretoria[p.id]);
      if (av > 0) { novo[p.id] = numeroParaMoeda(Math.round(av * 1.2 * 100) / 100); n++; }
    });
    if (!n) { toast('Nenhum produto com preço à vista definido ainda.', 'info'); return; }
    setPrecos12x(prev => ({ ...prev, ...novo }));
    toast(`12x aplicado em ${n} produto(s) (à vista × 1,2). Salvando...`, 'success');
  };

  // ---- Busca ----
  const buscarProdutos = async () => {
    if (!buscaCodpro && !buscaClasses.length && !buscaDescricao && !buscaDisponibilidades.length) {
      toast('Preencha ao menos um filtro (código, classe, disponibilidade ou descrição).', 'info');
      return;
    }
    const params = new URLSearchParams();
    if (buscaCodpro) params.append('codpro', buscaCodpro);
    if (buscaClasses.length) params.append('classes', buscaClasses.join(','));
    if (buscaDisponibilidades.length) params.append('disponibilidades', buscaDisponibilidades.join(','));
    if (buscaDescricao) params.append('descricao', buscaDescricao);
    if (buscaComEstoque) params.append('com_estoque', '1');
    setBuscandoProdutos(true);
    try {
      const res = await axios.get(`${API}/produtos/buscar?${params}`);
      setResultadosBusca(res.data);
      setSelecionados([]);
      setBuscou(true);
    } catch (err) {
      setResultadosBusca([]);
      setBuscou(true);
      Swal.fire('Erro', err.response?.data?.error || 'Erro ao buscar produtos. Tente de novo.', 'error');
    } finally {
      setBuscandoProdutos(false);
    }
  };

  // Mapeia um resultado de busca pro formato de staging/insert
  const toStaging = (r) => ({
    codpro: r.codpro, descricao: r.descricao, descricao_longa: r.descricao_longa, classe: r.clasprod,
    classe_nome: r.classe_nome, disponibilidade: r.disponibilidade,
    custo: r.custo, preco_sistema: r.preco_venda
  });
  const jaNaCampanha = (cod) => produtos.some(p => p.codpro?.trim() === cod);
  const jaNaLista = (cod) => paraAdicionar.some(p => p.codpro?.trim() === cod);

  // Código rápido: digita + Enter → busca exato e joga na lista de staging
  const addCodigoRapido = async () => {
    const cod = codigoRapido.trim();
    if (!cod) return;
    if (jaNaCampanha(cod)) { Swal.fire({ icon: 'info', title: 'Já está na campanha', text: `O código ${cod} já faz parte da campanha.`, timer: 1800, showConfirmButton: false }); setCodigoRapido(''); return; }
    if (jaNaLista(cod)) { Swal.fire({ icon: 'info', title: 'Já está na lista', timer: 1200, showConfirmButton: false }); setCodigoRapido(''); return; }
    setBuscandoCodigo(true);
    try {
      const res = await axios.get(`${API}/produtos/buscar`, { params: { codpro: cod } });
      const exato = res.data.find(r => r.codpro?.trim() === cod) || (res.data.length === 1 ? res.data[0] : null);
      if (!exato) {
        Swal.fire({ icon: 'warning', title: 'Código não encontrado', text: `Nenhum produto com o código ${cod}.`, timer: 1800, showConfirmButton: false });
      } else {
        setParaAdicionar(prev => [...prev, toStaging(exato)]);
        setCodigoRapido('');
      }
    } catch {
      Swal.fire('Erro', 'Erro ao buscar código', 'error');
    } finally {
      setBuscandoCodigo(false);
    }
  };

  // Importar planilha: códigos na 1ª coluna → mesma lista de staging do código rápido.
  // Nada é salvo aqui; a pessoa revisa a lista e salva no final, como no resto do modal.
  const importarPlanilha = async (file) => {
    if (!file) return;
    setImportandoPlanilha(true);
    try {
      const valores = await lerPrimeiraColuna(file);
      const codigos = [];
      const ignorados = [];
      valores.forEach(v => {
        const s = v.replace(/\.0+$/, '');
        // Código do ERP tem 5 dígitos; o Excel come o zero da frente (03414 → 3414).
        if (/^\d{1,5}$/.test(s)) codigos.push(s.padStart(5, '0'));
        else if (!/^c[oó]d/i.test(s)) ignorados.push(s); // cabeçalho "Código" não conta
      });
      const unicos = [...new Set(codigos)];
      if (!unicos.length) {
        Swal.fire('Nenhum código encontrado', 'A 1ª coluna da planilha precisa ter os códigos dos produtos (um por linha).', 'warning');
        return;
      }
      const res = await axios.post(`${API}/produtos/por-codigos`, { codigos: unicos });
      const naCampanha = [];
      const naLista = [];
      const novos = [];
      res.data.produtos.forEach(r => {
        const cod = r.codpro?.trim();
        if (jaNaCampanha(cod)) naCampanha.push(cod);
        else if (jaNaLista(cod)) naLista.push(cod);
        else novos.push(toStaging(r));
      });
      setParaAdicionar(prev => [...prev, ...novos]);

      const naoEncontrados = res.data.nao_encontrados || [];
      // Texto que veio da planilha vai pro HTML do alerta — escapa antes.
      const escapeHtml = (s) => String(s).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
      const lista = (arr, max = 40) => arr.slice(0, max).join(', ') + (arr.length > max ? ` e mais ${arr.length - max}` : '');
      const linhas = [
        `<div><b>${novos.length}</b> produto${novos.length === 1 ? '' : 's'} na lista pra adicionar</div>`,
        naCampanha.length ? `<div class="text-muted">${naCampanha.length} já estava${naCampanha.length === 1 ? '' : 'm'} na campanha</div>` : '',
        naLista.length ? `<div class="text-muted">${naLista.length} já estava${naLista.length === 1 ? '' : 'm'} na lista</div>` : '',
        codigos.length > unicos.length ? `<div class="text-muted">${codigos.length - unicos.length} repetido${codigos.length - unicos.length === 1 ? '' : 's'} na planilha</div>` : '',
        naoEncontrados.length ? `<div class="mt-2" style="color:#b23b3b"><b>${naoEncontrados.length} não encontrado${naoEncontrados.length === 1 ? '' : 's'} no ERP:</b><br><small>${lista(naoEncontrados)}</small></div>` : '',
        ignorados.length ? `<div class="mt-2" style="color:#8a6d00"><b>${ignorados.length} linha${ignorados.length === 1 ? '' : 's'} ignorada${ignorados.length === 1 ? '' : 's'}</b> (não parece código):<br><small>${lista(ignorados.map(escapeHtml), 10)}</small></div>` : '',
      ].filter(Boolean).join('');
      Swal.fire({
        icon: naoEncontrados.length || ignorados.length ? 'warning' : 'success',
        title: 'Planilha importada',
        html: `<div style="text-align:left;font-size:0.92rem">${linhas}<div class="mt-3 text-muted" style="font-size:0.8rem">Revise a lista e clique em <b>Salvar na campanha</b>.</div></div>`,
      });
    } catch (err) {
      Swal.fire('Erro ao importar', err.response?.data?.error || err.message || 'Não foi possível ler a planilha.', 'error');
    } finally {
      setImportandoPlanilha(false);
      if (inputPlanilhaRef.current) inputPlanilhaRef.current.value = ''; // permite importar o mesmo arquivo de novo
    }
  };

  // Joga os selecionados da busca por filtros na lista de staging (sem salvar ainda)
  const adicionarSelecionadosNaLista = () => {
    const novos = resultadosBusca
      .filter(r => selecionados.includes(r.codpro) && !jaNaLista(r.codpro?.trim()) && !jaNaCampanha(r.codpro?.trim()))
      .map(toStaging);
    setParaAdicionar(prev => [...prev, ...novos]);
    setSelecionados([]);
  };

  const removerDaLista = (cod) => setParaAdicionar(prev => prev.filter(p => p.codpro?.trim() !== cod));

  const fecharBusca = () => {
    setShowBusca(false);
    setParaAdicionar([]); setResultadosBusca([]); setSelecionados([]); setCodigoRapido('');
    setBuscou(false); setBuscaCodpro(''); setBuscaClasses([]); setClasseInput('');
    setBuscaDescricao(''); setBuscaDisponibilidades([]); setDispInput(''); setBuscaComEstoque(false);
  };

  // ---- Remoção em lote (limpeza por disponibilidade / classe / sem estoque) ----
  // Disponibilidades que existem hoje na campanha, com a contagem de cada uma.
  const disponibsNaCampanha = () => {
    const m = new Map();
    produtos.forEach(p => { const d = p.disponibilidade || 'N/D'; m.set(d, (m.get(d) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]); // [disp, count]
  };
  // Classes que existem hoje na campanha, com a contagem de cada uma.
  const classesNaCampanha = () => {
    const m = new Map();
    produtos.forEach(p => { const c = p.classe_nome || 'Sem classe'; m.set(c, (m.get(c) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]); // [classe_nome, count]
  };
  // Produtos da campanha que casam os critérios de remoção escolhidos.
  // A classe (se marcada) é um filtro que RESTRINGE (E) em cima do resto. Regra do resto:
  //  - disponibilidade(s) + "sem estoque" -> só os marcados QUE TAMBÉM estão sem estoque
  //  - só disponibilidade(s) -> todos os marcados
  //  - só "sem estoque" -> todos sem estoque
  //  - só classe -> todos daquela(s) classe(s)
  const produtosParaRemover = () => {
    const temDisp = remDisponibs.length > 0;
    const temClasse = remClasses.length > 0;
    return produtos.filter(p => {
      if (temClasse && !remClasses.includes(p.classe_nome || 'Sem classe')) return false; // filtro de classe (E)
      const dispMatch = remDisponibs.includes(p.disponibilidade || 'N/D');
      // "sem estoque" = sem estoque disponível na CENTRAL (filial 04), não a soma das lojas
      const semEstoque = (getEstoque(p.codpro)?.estoque_central || 0) <= 0;
      if (temDisp && remSemEstoque) return dispMatch && semEstoque;
      if (temDisp) return dispMatch;
      if (remSemEstoque) return semEstoque;
      if (temClasse) return true; // só classe marcada, sem outro critério -> remove a classe toda
      return false;
    });
  };

  // Lista final = os que casam os critérios MENOS os que o usuário decidiu manter
  const produtosParaRemoverFinal = () => produtosParaRemover().filter(p => !remExcluidos.includes(p.id));

  const removerLote = async () => {
    const alvo = produtosParaRemoverFinal();
    if (!alvo.length) { toast('Nenhum produto na lista de remoção.', 'info'); return; }
    const confirma = await Swal.fire({
      title: `Remover ${alvo.length} produto(s)?`,
      html: `Vai tirar <b>${alvo.length}</b> produto(s) da campanha. Dá pra adicionar de novo depois, mas não tem "desfazer".`,
      icon: 'warning', showCancelButton: true, confirmButtonText: `Remover ${alvo.length}`, cancelButtonText: 'Cancelar', confirmButtonColor: '#b23b3b',
    });
    if (!confirma.isConfirmed) return;
    setRemovendoLote(true);
    try {
      const res = await axios.post(`${API}/produtos/campanha/${id}/remover-lote`, { produto_ids: alvo.map(p => p.id) });
      const puladas = res.data.sincronizados ? ` · ${res.data.sincronizados} pulado(s) por já estarem no ERP` : '';
      Swal.fire({ icon: 'success', title: `${res.data.removidos} removido(s)${puladas}`, timer: 2400, showConfirmButton: false });
      setShowRemoverLote(false); setRemDisponibs([]); setRemClasses([]); setRemSemEstoque(false); setRemExcluidos([]);
      carregar();
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Erro ao remover em lote', 'error');
    } finally {
      setRemovendoLote(false);
    }
  };

  // Salva tudo que está na lista de staging de uma vez
  const salvarLista = async () => {
    if (!paraAdicionar.length || salvandoLista) return;
    setSalvandoLista(true);
    try {
      const res = await axios.post(`${API}/produtos/campanha/${id}`, { produtos: paraAdicionar });
      Swal.fire({ icon: 'success', title: `${res.data.inseridos} produtos adicionados!`, timer: 1500, showConfirmButton: false });
      fecharBusca();
      carregar();
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Erro ao adicionar', 'error');
    } finally {
      setSalvandoLista(false);
    }
  };

  // ---- Sincronizar com o ERP (cria a oferta se não existe, senão adiciona/atualiza) ----
  // produtoId = null -> todos os aprovados; produtoId -> só aquele.
  const sincronizarERP = async (produtoId = null) => {
    const aprovadosList = produtos.filter(p => aprovados[p.id]);
    if (!aprovadosList.length) return Swal.fire('Atenção', 'Nenhum produto aprovado para sincronizar', 'warning');
    const jaEnviada = !!campanha?.erp_oferta_oid;

    if (!produtoId) {
      const confirmText = (jaEnviada
          ? `Sincronizar <b>${aprovadosList.length}</b> produto(s) aprovado(s) com a oferta existente (adiciona novos e atualiza preço/quantidade dos demais).`
          : `Criar a oferta no ERP e enviar <b>${aprovadosList.length}</b> produto(s) aprovado(s):`) +
        `<br><br><b>À vista:</b> "${campanha.nome.toUpperCase()}"` +
        (tem12x ? `<br><b>12x:</b> "${campanha.nome.toUpperCase()} 12X"` : '') +
        `<br><br>Período: ${new Date(campanha.data_inicio).toLocaleDateString('pt-BR', { timeZone: 'UTC' })} a ${campanha.data_fim ? new Date(campanha.data_fim).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'}`;
      const result = await Swal.fire({
        title: jaEnviada ? 'Sincronizar com o ERP?' : 'Criar oferta no ERP?',
        html: confirmText, icon: 'question', showCancelButton: true,
        confirmButtonText: 'Sincronizar', cancelButtonText: 'Cancelar', confirmButtonColor: '#6B2C5C',
      });
      if (!result.isConfirmed) return;
    }

    produtoId ? setSincronizandoUm(produtoId) : setEnviandoERP(true);
    try {
      const res = await axios.post(`${API}/erp/campanha/${id}/sincronizar`, produtoId ? { produto_id: produtoId } : {});
      const data = res.data;
      let msg = `<b>${data.sincronizados}</b> produto(s) sincronizado(s)${data.criou ? ' · oferta criada' : ''}.`;
      if (data.erros?.length) msg += `<br><br><small style="color:#c00">Avisos:<br>${data.erros.join('<br>')}</small>`;
      await Swal.fire({ icon: data.erros?.length ? 'warning' : 'success', title: 'Sincronizado com o ERP', html: msg });
      carregar();
    } catch (err) {
      const errData = err.response?.data;
      Swal.fire('Erro ao sincronizar', errData?.detalhes || errData?.error || 'Erro na integração com o ERP', 'error');
    } finally {
      produtoId ? setSincronizandoUm(null) : setEnviandoERP(false);
    }
  };

  // ---- Encerrar produto no ERP (iguala ofertada à vendida -> saldo 0) ----
  const encerrarProduto = async (produtoId, descricao) => {
    const r = await Swal.fire({
      title: 'Encerrar no ERP?',
      html: `Iguala a quantidade <b>ofertada</b> à já <b>vendida</b> de "${descricao}" — zera o saldo e encerra a oferta desse produto (sem removê-lo).`,
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Encerrar', cancelButtonText: 'Cancelar', confirmButtonColor: '#b53d3d',
    });
    if (!r.isConfirmed) return;
    setSincronizandoUm(produtoId);
    try {
      const res = await axios.post(`${API}/erp/campanha/${id}/produto/${produtoId}/encerrar`);
      await Swal.fire({ icon: 'success', title: 'Produto encerrado', text: `Quantidade ofertada igualada à vendida (${res.data.vendida}).`, timer: 2200, showConfirmButton: false });
      carregar();
    } catch (err) {
      const errData = err.response?.data;
      Swal.fire('Erro ao encerrar', errData?.detalhes || errData?.error || 'Erro na integração com o ERP', 'error');
    } finally {
      setSincronizandoUm(null);
    }
  };

  // ---- Salvar ----
  // Monta o payload dos preenchimentos da diretoria (usado no save manual e no auto-save)
  const estadoDiretoria = () => ({ d: precosDiretoria, x: precos12x, c: comissoes, q: qtdOfertadas, a: aprovados, z: precosPrazo, pc: percsPrazo });
  estadoRef.current = estadoDiretoria();
  replicarSempreRef.current = replicarSempre;
  const montarUpdateDiretoria = () => produtos.map(p => linhaDiretoria(p.id, estadoRef.current));
  // Assinatura do estado de preenchimento — pra detectar mudança real (auto-save)
  const sigDiretoria = () => JSON.stringify(estadoDiretoria());

  const salvarTudo = async () => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); // cancela auto-save pendente
    setSalvando(true);
    try {
      await axios.put(`${API}/produtos/campanha/${id}/lote`, { produtos: montarUpdateDiretoria() });
      savedSigRef.current = sigDiretoria();
      setAutoSaveStatus('saved');
      Swal.fire({ icon: 'success', title: 'Salvo!', timer: 1200, showConfirmButton: false });
      carregar();
    } catch (err) {
      setAutoSaveStatus('erro');
      Swal.fire('Erro', err.response?.data?.error || 'Erro ao salvar', 'error');
    } finally {
      setSalvando(false);
    }
  };

  // Auto-save silencioso (sem Swal, sem recarregar — não perde foco/rolagem).
  // Manda só as linhas que mudaram: com várias listas abertas ao mesmo tempo, mandar a lista
  // inteira regravaria por cima um preço replicado de outra aba.
  const autoSalvarTudo = async (sig) => {
    const linhas = montarUpdateDiretoria().filter(l => savedLinhasRef.current[l.id] !== JSON.stringify(l));
    if (!linhas.length) { savedSigRef.current = sig; setAutoSaveStatus('saved'); return; }
    setAutoSaveStatus('saving');
    try {
      await axios.put(`${API}/produtos/campanha/${id}/lote`, { produtos: linhas });
      linhas.forEach(l => { savedLinhasRef.current[l.id] = JSON.stringify(l); });
      savedSigRef.current = sig;
      setAutoSaveStatus('saved');
    } catch (err) {
      setAutoSaveStatus('erro');
    }
  };

  // Dispara o auto-save da diretoria ~1,2s depois da última alteração
  useEffect(() => {
    if (loading || !podeDiretoria || isEncerrada) return;
    const sig = sigDiretoria();
    if (sig === savedSigRef.current) return; // nada mudou desde o load/save
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    // Com o campo ainda em foco, espera mais: evita gravar um valor no meio da digitação
    // (a máscara é de centavos — "12,50" passa por 0,01 / 0,12 / 1,25 no caminho).
    autoSaveTimer.current = setTimeout(() => autoSalvarTudo(sig), campoEmEdicao ? 3000 : 1200);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [precosDiretoria, precos12x, comissoes, qtdOfertadas, aprovados, precosPrazo, percsPrazo, loading, podeDiretoria, campoEmEdicao]);

  // Auto-save das sugestões do gerente
  useEffect(() => {
    if (loading || isEncerrada || !(veSugestoes && temPermissao('sugestoes_preencher'))) return;
    const sig = JSON.stringify(sugestoesLocal);
    if (sig === savedSugSigRef.current) return;
    const validas = Object.entries(sugestoesLocal).filter(([, v]) => v && parseFloat(v) > 0);
    if (!validas.length) { savedSugSigRef.current = sig; return; } // nada válido pra salvar ainda
    if (autoSaveSugTimer.current) clearTimeout(autoSaveSugTimer.current);
    autoSaveSugTimer.current = setTimeout(async () => {
      setAutoSaveStatus('saving');
      try {
        await axios.post(`${API}/sugestoes/campanha/${id}`, { sugestoes: validas.map(([cid, preco]) => ({ campanha_produto_id: parseInt(cid), preco_sugerido: parseFloat(preco) })) });
        savedSugSigRef.current = sig;
        setAutoSaveStatus('saved');
      } catch { setAutoSaveStatus('erro'); }
    }, 1200);
    return () => { if (autoSaveSugTimer.current) clearTimeout(autoSaveSugTimer.current); };
  }, [sugestoesLocal, loading]);

  // ---- Replicar preço nas outras listas ----
  // Valores que vão pra outra lista: à vista + 12x + comissão. Sem 12x nesta campanha, manda
  // vazio e a outra lista (se tiver 12x) fica com o padrão à vista × 1,2.
  const valoresProduto = (pid) => {
    const s = estadoRef.current;
    return { preco_diretoria: parseMoedaOuNull(s.d[pid]), preco_12x: tem12x ? parseMoedaOuNull(s.x[pid]) : null, comissao: parseMoedaOuNull(s.c[pid]) };
  };

  // Ref e não o estado: a checagem chega de um timeout, com o estado de um render antigo
  const abrirAvisoReplicar = (aviso) => {
    if (replicarAbertoRef.current) { filaReplicarRef.current.push(aviso); return; }
    replicarAbertoRef.current = aviso;
    setReplicar(aviso);
  };
  const fecharAvisoReplicar = () => {
    const prox = filaReplicarRef.current.shift() || null;
    replicarAbertoRef.current = prox;
    setReplicar(prox);
  };
  const confirmarReplicar = async (selecionados, sempre) => {
    await executarReplicacao(selecionados);
    if (sempre) {
      setReplicarSempre(true);
      replicarSempreRef.current = true;
      // Avisos de produto que já estavam na fila passam a replicar direto
      const fila = filaReplicarRef.current.filter(a => a.modo === 'um');
      filaReplicarRef.current = filaReplicarRef.current.filter(a => a.modo !== 'um');
      if (fila.length) await executarReplicacao(fila.flatMap(a => a.itens), true);
    }
    fecharAvisoReplicar();
  };

  const executarReplicacao = async (itens, automatico = false) => {
    setReplicando(true);
    try {
      const r = await axios.post(`${API}/produtos/campanha/${id}/replicar-precos`, {
        itens: itens.map(it => ({ codpro: it.codpro, ...it.valores, destinos: it.destinos.map(d => d.produto_id) }))
      });
      const { atualizados, campanhas } = r.data;
      const nomes = campanhas.map(c => c.nome).join(', ');
      const erp = campanhas.filter(c => c.precisa_sincronizar).map(c => c.nome);
      if (erp.length) {
        Swal.fire({ toast: true, position: 'top-end', icon: 'warning', showConfirmButton: false, timer: 8000, timerProgressBar: true,
          title: `Replicado em ${atualizados} lugar(es). Já estão no ERP e precisam de "Sincronizar": ${erp.join(', ')}` });
      } else {
        toast(`${automatico ? 'Replicado automaticamente' : 'Preço replicado'} em ${atualizados} lugar(es): ${nomes}`, 'success');
      }
    } catch (err) {
      Swal.fire('Erro ao replicar', err.response?.data?.error || 'Não foi possível replicar o preço nas outras listas', 'error');
    } finally {
      setReplicando(false);
    }
  };

  // Depois de mexer no preço de um produto: procura nas outras listas e avisa (ou replica direto)
  const verificarOutrasListas = async (p, valores) => {
    try {
      const r = await axios.post(`${API}/produtos/campanha/${id}/outras-listas`, { codpros: [String(p.codpro).trim()] });
      const destinos = r.data.filter(d => destinoDiverge(valores, d));
      if (!destinos.length) return;
      const item = { codpro: String(p.codpro).trim(), descricao: descTela(p), valores, destinos };
      if (replicarSempreRef.current) await executarReplicacao([item], true);
      else abrirAvisoReplicar({ modo: 'um', itens: [item] });
    } catch { /* o aviso é ajuda — falhar aqui não pode atrapalhar a digitação */ }
  };

  // Entrar nos campos de preço de uma linha guarda os valores; a checagem roda ao SAIR da linha
  // (Tab do Preço pro 12x ainda é a mesma linha — não pergunta no meio).
  const focoPreco = (pid, campo) => {
    setCampoEmEdicao(`${pid}:${campo}`);
    campoEmEdicaoRef.current = `${pid}:${campo}`;
    if (linhaEmEdicaoRef.current?.id !== pid) linhaEmEdicaoRef.current = { id: pid, antes: JSON.stringify(valoresProduto(pid)) };
  };
  const saidaPreco = (p) => {
    setCampoEmEdicao(null);
    campoEmEdicaoRef.current = null;
    const linha = linhaEmEdicaoRef.current;
    if (!linha || linha.id !== p.id || !podeDiretoria) return;
    setTimeout(() => {
      if (campoEmEdicaoRef.current?.startsWith(`${p.id}:`)) return; // foi pra outro campo da mesma linha
      if (linhaEmEdicaoRef.current?.id === p.id) linhaEmEdicaoRef.current = null;
      const valores = valoresProduto(p.id);
      if (JSON.stringify(valores) === linha.antes || !(valores.preco_diretoria > 0)) return;
      verificarOutrasListas(p, valores);
    }, 250);
  };

  // Ações → Alinhar com outras listas: todos os produtos com preço que divergem em outra lista
  const alinharComOutrasListas = async () => {
    const comPreco = produtos.filter(p => numDir(p.id) > 0);
    if (!comPreco.length) { toast('Nenhum produto com preço definido nesta lista.', 'info'); return; }
    try {
      const r = await axios.post(`${API}/produtos/campanha/${id}/outras-listas`, { codpros: comPreco.map(p => String(p.codpro).trim()) });
      const porCodpro = {};
      r.data.forEach(d => { (porCodpro[d.codpro] = porCodpro[d.codpro] || []).push(d); });
      const itens = comPreco.map(p => {
        const codpro = String(p.codpro).trim();
        const valores = valoresProduto(p.id);
        const destinos = (porCodpro[codpro] || []).filter(d => destinoDiverge(valores, d));
        return destinos.length ? { codpro, descricao: descTela(p), valores, destinos } : null;
      }).filter(Boolean);
      if (!itens.length) { toast('Tudo alinhado: nenhuma outra lista aberta do período está com preço diferente.', 'success'); return; }
      abrirAvisoReplicar({ modo: 'lote', itens });
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível consultar as outras listas', 'error');
    }
  };

  // Voltou pra esta aba (ex.: replicou preço em outra lista aberta noutra aba)? Recarrega,
  // se não houver nada sendo digitado nem esperando salvar — senão a tela mostra preço velho.
  const recarregarAoVoltarRef = useRef(null);
  recarregarAoVoltarRef.current = () => {
    if (document.visibilityState !== 'visible' || loading || salvando || replicar || replicando) return;
    if (campoEmEdicaoRef.current || JSON.stringify(estadoRef.current) !== savedSigRef.current) return;
    carregar();
  };
  useEffect(() => {
    const aoVoltar = () => recarregarAoVoltarRef.current?.();
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, []);

  // ---- Sugestões gerente ----
  const salvarSugestoes = async () => {
    const sugs = Object.entries(sugestoesLocal).filter(([, v]) => v && parseFloat(v) > 0)
      .map(([campanhaProdutoId, preco]) => ({ campanha_produto_id: parseInt(campanhaProdutoId), preco_sugerido: parseFloat(preco) }));
    if (!sugs.length) return Swal.fire('Atenção', 'Preencha ao menos uma sugestão', 'warning');
    try {
      await axios.post(`${API}/sugestoes/campanha/${id}`, { sugestoes: sugs });
      Swal.fire({ icon: 'success', title: 'Sugestões salvas!', timer: 1500, showConfirmButton: false });
      carregar();
      setSugestoesLocal({});
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Erro', 'error');
    }
  };

  // ---- Lote desconto ----
  // Interpreta o campo de terminação: aceita "90", "99", "0,90", ",90" -> centavos (0..99). Vazio = sem arredondar.
  const parseTerminacao = (v) => {
    if (v == null || !String(v).trim()) return null;
    let n = parseFloat(String(v).trim().replace(',', '.'));
    if (isNaN(n)) return null;
    if (n > 0 && n < 1) n = n * 100; // 0,90 -> 90
    n = Math.round(n);
    if (n < 0 || n > 99) return null;
    return n;
  };

  const simularLote = async () => {
    if (!descontoPerc || simulandoLote) return;
    const terminacao = parseTerminacao(terminacaoLote);
    if (terminacaoLote.trim() && terminacao == null) { Swal.fire('Terminação inválida', 'Use um valor de centavos entre 0 e 99 (ex: 90 ou 99).', 'warning'); return; }
    setSimulandoLote(true);
    try {
      const res = await axios.post(`${API}/produtos/campanha/${id}/simular-lote`, { desconto_percentual: parseFloat(descontoPerc), classe: classeLote || null, terminacao });
      setPreviewLote(res.data);
    } catch (err) { Swal.fire('Erro', 'Erro ao simular', 'error'); }
    finally { setSimulandoLote(false); }
  };

  const aplicarLote = async () => {
    if (aplicandoLote) return;
    setAplicandoLote(true);
    try {
      await axios.put(`${API}/produtos/campanha/${id}/aplicar-lote`, { produtos: previewLote.map(p => ({ id: p.id, novo_preco: p.novo_preco })) });
      Swal.fire({ icon: 'success', title: 'Desconto aplicado!', timer: 1500, showConfirmButton: false });
      setShowLote(false); setPreviewLote([]); setTerminacaoLote(''); carregar();
    } catch (err) { Swal.fire('Erro', 'Erro ao aplicar', 'error'); }
    finally { setAplicandoLote(false); }
  };

  // ---- Filiais excluídas ----
  const toggleFilialExcluida = async (filialCodigo) => {
    const cod = parseInt(filialCodigo);
    if (filiaisExcluidas.includes(cod)) {
      await axios.delete(`${API}/campanhas/${id}/filiais-excluidas/${cod}`);
    } else {
      await axios.post(`${API}/campanhas/${id}/filiais-excluidas`, { filial_codigo: cod });
    }
    carregar();
  };

  const atualizarPrecosSistema = async () => {
    try {
      const res = await axios.post(`${API}/produtos/campanha/${id}/atualizar-precos`);
      const { atualizados, produtosAlterados } = res.data;
      const comSugestao = (produtosAlterados || []).filter(p => p.tem_sugestoes);

      if (comSugestao.length > 0) {
        const lista = comSugestao.map(p => `<tr><td style="padding:2px 8px">${p.codpro}</td><td style="padding:2px 8px">${p.descricao_longa || p.descricao}</td><td style="padding:2px 8px">R$ ${p.preco_antigo.toFixed(2)} → R$ ${p.preco_novo.toFixed(2)}</td></tr>`).join('');
        await Swal.fire({
          icon: 'warning',
          title: 'Preços alterados!',
          html: `<b>${comSugestao.length}</b> produto(s) tiveram preço atualizado e <b>já possuem sugestões de gerente</b>. O markup pode ter mudado.<br><br>` +
            `<table style="width:100%;font-size:0.85rem;text-align:left"><thead><tr><th style="padding:2px 8px">Código</th><th style="padding:2px 8px">Descrição</th><th style="padding:2px 8px">Preço</th></tr></thead><tbody>${lista}</tbody></table>`,
          confirmButtonColor: '#6B2C5C',
        });
      } else {
        Swal.fire({ icon: 'success', title: `${atualizados} preços atualizados!`, timer: 1500, showConfirmButton: false });
      }
      carregar(); carregarEstoque();
    } catch (err) { Swal.fire('Erro', 'Erro ao atualizar preços', 'error'); }
  };

  const removerProduto = async (produtoId, descricao) => {
    const r = await Swal.fire({ title: `Remover "${descricao}"?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc3545', confirmButtonText: 'Remover', cancelButtonText: 'Cancelar' });
    if (!r.isConfirmed) return;
    await axios.delete(`${API}/produtos/campanha/${id}/produto/${produtoId}`);
    carregar();
  };

  // ---- Histórico ----
  const abrirHistorico = async (produtoId) => {
    try {
      const res = await axios.get(`${API}/produtos/campanha/${id}/produto/${produtoId}/historico`);
      setHistorico(res.data);
      setShowHistorico(produtoId);
    } catch (err) { console.error(err); }
  };

  const abrirEstoqueFilial = async (codpro) => {
    setShowEstoqueFilial(codpro);
    // Rebusca sempre: o cache "uma vez só" ficava desatualizado quando produtos
    // eram adicionados depois (o produto novo não aparecia no modal por filial).
    setEstoquePorFilial(null);
    try {
      const res = await axios.get(`${API}/estoque/campanha/${id}/por-filial`);
      setEstoquePorFilial(res.data);
    } catch (err) {
      Swal.fire('Erro', 'Erro ao carregar estoque por filial', 'error');
      setShowEstoqueFilial(null);
    }
  };

  // ---- Status da campanha ----
  const statusCampanha = campanha?.status || 'rascunho';
  const isEncerrada = statusCampanha === 'encerrada';
  const isAtiva = statusCampanha === 'ativa';
  const podeEditar = !isEncerrada;
  const podeAdicionarRemover = statusCampanha === 'rascunho';
  // Adicionar produtos é permitido mesmo com a campanha ativa (só não quando encerrada).
  const podeAdicionar = !isEncerrada;

  const mudarStatus = async (novoStatus) => {
    const msgs = {
      ativa: { title: 'Ativar campanha?', text: 'Após ativar, não será possível adicionar ou remover produtos.', btn: 'Ativar', color: '#28a745' },
      encerrada: { title: 'Encerrar campanha?', text: 'A campanha ficará em modo somente leitura.', btn: 'Encerrar', color: '#6c757d' },
      rascunho: { title: 'Voltar para rascunho?', text: 'A campanha voltará a ser editável.', btn: 'Confirmar', color: '#6B2C5C' },
    };
    const m = msgs[novoStatus];
    const r = await Swal.fire({ title: m.title, text: m.text, icon: 'question', showCancelButton: true, confirmButtonText: m.btn, cancelButtonText: 'Cancelar', confirmButtonColor: m.color });
    if (!r.isConfirmed) return;
    try {
      await axios.put(`${API}/campanhas/${id}/status`, { status: novoStatus });
      Swal.fire({ icon: 'success', title: m.btn + '!', timer: 1500, showConfirmButton: false });
      carregar();
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Erro ao mudar status', 'error');
    }
  };

  const toggleVisivelGerente = async () => {
    const novo = !campanha.visivel_gerente;
    try {
      await axios.put(`${API}/campanhas/${id}/visivel-gerente`, { visivel_gerente: novo });
      Swal.fire({ icon: 'success', title: novo ? 'Liberada pros gerentes' : 'Oculta dos gerentes', timer: 1300, showConfirmButton: false });
      carregar();
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Erro ao alterar visibilidade', 'error');
    }
  };

  // Escolha do tipo de precificação (+ % padrão e faixas de arredondamento do modo a prazo).
  // Trocar o tipo não mexe nos preços já gravados. Mudar o % ou as faixas recalcula os produtos
  // com preço a prazo cujo Preço é o calculado; Preço digitado à mão (manual) fica como está.
  const editarPrecificacao = async () => {
    const percAtual = percCampanha != null ? String(percCampanha).replace('.', ',') : '';
    const c2 = (n) => String(n).padStart(2, '0');
    const linhaFaixa = (f = {}) => `
      <div class="tp-faixa" style="display:flex;align-items:center;gap:6px;margin-bottom:6px;font-size:0.82rem">
        de ,<input class="tp-de" inputmode="numeric" maxlength="2" value="${f.de != null ? c2(f.de) : ''}" placeholder="01" style="width:44px;padding:4px;border:1px solid #ccc;border-radius:5px">
        até ,<input class="tp-ate" inputmode="numeric" maxlength="2" value="${f.ate != null ? c2(f.ate) : ''}" placeholder="49" style="width:44px;padding:4px;border:1px solid #ccc;border-radius:5px">
        → arredonda para ,<input class="tp-para" inputmode="numeric" maxlength="2" value="${f.para != null ? c2(f.para) : ''}" placeholder="50" style="width:44px;padding:4px;border:1px solid #ccc;border-radius:5px">
        <button type="button" class="tp-rem" title="Remover faixa" style="border:none;background:none;color:#b53d3d;font-size:1rem;cursor:pointer">×</button>
      </div>`;
    const lerFaixas = () => [...document.querySelectorAll('.tp-faixa')].map(el => ({
      de: el.querySelector('.tp-de').value.trim(), ate: el.querySelector('.tp-ate').value.trim(), para: el.querySelector('.tp-para').value.trim(),
    })).filter(f => f.de !== '' || f.ate !== '' || f.para !== '');
    const { value: v } = await Swal.fire({
      title: 'Tipo de precificação',
      width: 560,
      html: `
        <div style="text-align:left;font-size:0.88rem">
          <label style="display:flex;gap:8px;align-items:flex-start;padding:8px;border:1px solid #eee;border-radius:8px;cursor:pointer;margin-bottom:8px">
            <input type="radio" name="tp-prec" value="padrao" ${!modoPrazo ? 'checked' : ''} style="margin-top:3px">
            <span><b>Padrão</b><br><span style="color:#777">A diretoria digita o Preço direto (como sempre foi).</span></span>
          </label>
          <label style="display:flex;gap:8px;align-items:flex-start;padding:8px;border:1px solid #eee;border-radius:8px;cursor:pointer">
            <input type="radio" name="tp-prec" value="prazo" ${modoPrazo ? 'checked' : ''} style="margin-top:3px">
            <span><b>A partir do preço a prazo</b><br><span style="color:#777">Digita o preço a prazo; o Preço = prazo × (1 − %), arredondado pelas faixas abaixo. O 12x segue o Preço × 1,2.</span></span>
          </label>
          <div id="tp-perc-box" style="margin-top:12px;display:${modoPrazo ? 'block' : 'none'}">
            <label style="font-weight:600;font-size:0.82rem">% de desconto padrão sobre o preço a prazo</label>
            <input id="tp-perc" class="swal2-input" style="margin:6px 0 0;width:140px;height:2.2em" inputmode="decimal" placeholder="Ex: 10" value="${percAtual}">
            <div style="color:#777;font-size:0.78rem;margin-top:4px">Cada produto pode ter um % próprio na coluna "%".</div>
            <div style="margin-top:14px;font-weight:600;font-size:0.82rem">Arredondamento dos centavos do Preço <span style="font-weight:400;color:#888">(opcional)</span></div>
            <div style="color:#777;font-size:0.78rem;margin:2px 0 8px">Dentro do mesmo real. Centavos fora das faixas não mudam. Preço digitado à mão não é arredondado.</div>
            <div id="tp-faixas">${faixasArred.map(linhaFaixa).join('')}</div>
            <button type="button" id="tp-add" style="border:1px dashed #6B2C5C;background:#fff;color:#6B2C5C;border-radius:6px;padding:3px 10px;font-size:0.8rem;cursor:pointer">+ faixa</button>
            <button type="button" id="tp-exemplo" style="border:none;background:none;color:#6B2C5C;text-decoration:underline;font-size:0.78rem;cursor:pointer;margin-left:6px">usar exemplo (,01–,49 → ,50 · ,51–,99 → ,90)</button>
            <div id="tp-preview" style="margin-top:8px;color:#555;font-size:0.8rem"></div>
          </div>
        </div>`,
      showCancelButton: true, confirmButtonText: 'Salvar', cancelButtonText: 'Cancelar', confirmButtonColor: '#6B2C5C',
      didOpen: () => {
        const box = document.getElementById('tp-faixas');
        const preview = () => {
          const fx = lerFaixas().map(f => ({ de: parseInt(f.de, 10), ate: parseInt(f.ate, 10), para: parseInt(f.para, 10) }))
            .filter(f => [f.de, f.ate, f.para].every(Number.isInteger));
          document.getElementById('tp-preview').innerHTML = fx.length
            ? 'Prévia: ' + [92.04, 92.49, 92.63, 92.95, 92.00].map(x => `${fmt(x)} → <b>${fmt(arredondarPreco(x, fx))}</b>`).join(' · ')
            : '<span style="color:#999">Sem arredondamento.</span>';
        };
        const ligar = () => {
          box.querySelectorAll('.tp-rem').forEach(b => { b.onclick = () => { b.closest('.tp-faixa').remove(); preview(); }; });
          box.querySelectorAll('input').forEach(i => { i.oninput = () => { i.value = i.value.replace(/\D/g, '').slice(0, 2); preview(); }; });
        };
        document.getElementById('tp-add').onclick = () => { box.insertAdjacentHTML('beforeend', linhaFaixa()); ligar(); };
        document.getElementById('tp-exemplo').onclick = () => { box.innerHTML = linhaFaixa({ de: 1, ate: 49, para: 50 }) + linhaFaixa({ de: 51, ate: 99, para: 90 }); ligar(); preview(); };
        ligar(); preview();
        document.querySelectorAll('input[name="tp-prec"]').forEach(r => r.addEventListener('change', () => {
          document.getElementById('tp-perc-box').style.display = document.querySelector('input[name="tp-prec"]:checked').value === 'prazo' ? 'block' : 'none';
        }));
      },
      preConfirm: () => {
        const tipo = document.querySelector('input[name="tp-prec"]:checked')?.value || 'padrao';
        if (tipo === 'padrao') return { tipo, perc: percCampanha, faixas: faixasArred };
        const raw = document.getElementById('tp-perc').value.trim().replace(',', '.');
        const perc = raw === '' ? NaN : parseFloat(raw);
        if (!Number.isFinite(perc) || perc < 0 || perc >= 100) { Swal.showValidationMessage('Informe o % entre 0 e 99,99'); return false; }
        const faixas = [];
        for (const f of lerFaixas()) {
          const de = parseInt(f.de, 10), ate = parseInt(f.ate, 10), para = parseInt(f.para, 10);
          if (![de, ate, para].every(Number.isInteger)) { Swal.showValidationMessage('Preencha de / até / para em todas as faixas (00 a 99)'); return false; }
          if (de > ate) { Swal.showValidationMessage(`Faixa ,${c2(de)}–,${c2(ate)}: o "de" não pode ser maior que o "até"`); return false; }
          faixas.push({ de, ate, para });
        }
        faixas.sort((a, b) => a.de - b.de);
        if (faixas.some((f, i) => i > 0 && f.de <= faixas[i - 1].ate)) { Swal.showValidationMessage('As faixas não podem se sobrepor'); return false; }
        return { tipo, perc: Math.round(perc * 100) / 100, faixas };
      },
    });
    if (!v) return;
    try {
      const res = await axios.put(`${API}/campanhas/${id}/precificacao`, { tipo_precificacao: v.tipo, perc_prazo: v.perc, arredondamento: v.faixas });
      // Quem é "manual" é decidido com a regra ANTIGA, antes de trocar o estado
      const afetados = v.tipo === 'prazo'
        ? produtos.filter(p => numPrazo(p.id) > 0 && !ehPrecoManual(p.id))
        : [];
      setCampanha(c => ({ ...c, tipo_precificacao: v.tipo, perc_prazo: v.perc, arredondamento: res.data?.arredondamento ?? null }));
      if (v.tipo === 'prazo') {
        let mudou = 0;
        afetados.forEach(p => {
          const perc = temPercProprio(p.id) ? parseMoeda(percsPrazo[p.id]) : v.perc;
          const novo = precoCalculado(numPrazo(p.id), perc, v.faixas);
          if (Math.abs(novo - numDir(p.id)) > 0.004) { recalcularDoPrazo(p.id, numPrazo(p.id), perc, v.faixas); mudou++; }
        });
        const manuais = produtos.filter(p => ehPrecoManual(p.id)).length;
        const semPrazo = produtos.filter(p => !(numPrazo(p.id) > 0)).length;
        toast(mudou
          ? `Preço recalculado em ${mudou} produto(s)${manuais ? ` · ${manuais} manual(is) mantido(s)` : ''}. Salvando...`
          : semPrazo ? 'Precificação a prazo ativada — preencha a coluna "A prazo".' : 'Precificação salva.', 'success');
      } else {
        toast('Precificação padrão — o Preço volta a ser digitado direto.', 'success');
      }
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Erro ao salvar a precificação', 'error');
    }
  };

  // Preço a prazo "do sistema" = preço de venda do ERP × (1 + taxa da forma de pagamento a prazo).
  // modo: 'auto' = só quem está sem prazo E sem Preço (não pisa no que foi digitado);
  //       'sem_prazo' = todos sem prazo; 'todos' = sobrescreve todos.
  const preencherPrazoSistema = async (modo) => {
    const res = await axios.get(`${API}/produtos/campanha/${id}/prazo-sistema`);
    setTaxaPrazoSistema(res.data.taxa);
    const sugeridos = res.data.produtos || {};
    const alvo = produtos.filter(p => {
      if (!(sugeridos[p.id] > 0)) return false;
      if (modo === 'todos') return true;
      if (modo === 'sem_prazo') return !(numPrazo(p.id) > 0);
      return !(numPrazo(p.id) > 0) && !(numDir(p.id) > 0);
    });
    if (!alvo.length) return 0;
    setPrecosPrazo(prev => {
      const novo = { ...prev };
      alvo.forEach(p => { novo[p.id] = numeroParaMoeda(sugeridos[p.id]); });
      return novo;
    });
    alvo.forEach(p => recalcularDoPrazo(p.id, sugeridos[p.id], percEfetivo(p.id)));
    return alvo.length;
  };

  // Ação do menu: preencher/recalcular o preço a prazo do sistema, com confirmação
  const acaoPrazoSistema = async () => {
    const semPrazo = produtos.filter(p => !(numPrazo(p.id) > 0)).length;
    const { value: modo } = await Swal.fire({
      title: 'Preço a prazo do sistema',
      html: `<div style="text-align:left;font-size:0.88rem">
          <p style="color:#666;margin-bottom:10px">Preço de venda do ERP + ${taxaPrazoSistema != null ? fmt(taxaPrazoSistema) + '%' : 'taxa'} da forma de pagamento a prazo. O Preço e o 12x são recalculados com o % de cada produto.</p>
          <label style="display:flex;gap:8px;padding:8px;border:1px solid #eee;border-radius:8px;cursor:pointer;margin-bottom:8px">
            <input type="radio" name="pz-modo" value="sem_prazo" checked> <span><b>Só os sem preço a prazo</b> (${semPrazo})</span>
          </label>
          <label style="display:flex;gap:8px;padding:8px;border:1px solid #eee;border-radius:8px;cursor:pointer">
            <input type="radio" name="pz-modo" value="todos"> <span><b>Todos — sobrescreve</b> o que foi digitado (${produtos.length})</span>
          </label></div>`,
      showCancelButton: true, confirmButtonText: 'Aplicar', cancelButtonText: 'Cancelar', confirmButtonColor: '#6B2C5C',
      preConfirm: () => document.querySelector('input[name="pz-modo"]:checked')?.value,
    });
    if (!modo) return;
    try {
      const n = await preencherPrazoSistema(modo);
      toast(n ? `Preço a prazo do sistema aplicado em ${n} produto(s). Salvando...` : 'Nenhum produto para preencher.', n ? 'success' : 'info');
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Erro ao buscar o preço a prazo do sistema', 'error');
    }
  };

  // Preenchimento automático: na campanha a prazo, produto sem prazo e sem Preço recebe o prazo
  // do sistema (produto recém-adicionado, ou campanha que acabou de virar "a prazo"). O auto-save grava.
  useEffect(() => {
    if (loading || !modoPrazo || !podeDiretoria || isEncerrada || !produtos.length) return;
    const pendentes = produtos.filter(p => !autoPrazoTentadosRef.current.has(p.id) && !(numPrazo(p.id) > 0) && !(numDir(p.id) > 0));
    if (!pendentes.length) return;
    pendentes.forEach(p => autoPrazoTentadosRef.current.add(p.id));
    preencherPrazoSistema('auto')
      .then(n => { if (n) toast(`Preço a prazo do sistema preenchido em ${n} produto(s).`, 'success'); })
      .catch(() => {});
  }, [loading, modoPrazo, podeDiretoria, isEncerrada, produtos]);

  // Taxa a prazo do ERP pra exibir no cabeçalho/menu (se o preenchimento automático não buscou)
  useEffect(() => {
    if (!modoPrazo || taxaPrazoSistema != null) return;
    axios.get(`${API}/produtos/campanha/${id}/prazo-sistema`).then(r => setTaxaPrazoSistema(r.data.taxa)).catch(() => {});
  }, [modoPrazo, taxaPrazoSistema, id]);

  if (loading) return <div className="text-center py-5"><div className="spinner-border" style={{ color: '#6B2C5C' }} /></div>;
  if (!campanha) return <div className="text-center py-5 text-muted">Campanha não encontrada</div>;

  return (
    <div>
      {/* Header — linha 1: identidade + status workflow */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate('/campanhas')} title="Voltar">
            <i className="bi bi-arrow-left"></i>
          </button>
          <span className="fw-bold fs-5">{campanha.nome}</span>
          <span className={`badge-${campanha.status}`}>{campanha.status}</span>
          {tem12x && <span className="badge-aprovacao">12X</span>}
          {campanha?.erp_oferta_oid && (
            <Tooltip texto={campanha.erp_enviado_em ? `Enviado em ${new Date(campanha.erp_enviado_em).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}` : 'Enviado para o ERP'}>
              <span className="badge" style={{ backgroundColor: '#28a745', fontSize: '0.78rem', padding: '4px 8px' }}>
                <i className="bi bi-check-circle me-1"></i>ERP
              </span>
            </Tooltip>
          )}
          {campanha.data_inicio && (
            <small className="text-muted ms-1">
              <i className="bi bi-calendar3 me-1"></i>
              {new Date(campanha.data_inicio).toLocaleDateString('pt-BR', { timeZone: 'UTC' })} → {campanha.data_fim ? new Date(campanha.data_fim).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'}
            </small>
          )}
        </div>
        {temPermissao('campanhas_editar') && (
          <div className="d-flex gap-1">
            {statusCampanha === 'rascunho' && (
              <button className="btn btn-sm btn-success" onClick={() => mudarStatus('ativa')}><i className="bi bi-play-fill me-1"></i>Ativar</button>
            )}
            {isAtiva && (
              <>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => mudarStatus('rascunho')} title="Voltar a rascunho"><i className="bi bi-arrow-counterclockwise"></i></button>
                <button className="btn btn-sm btn-outline-danger" onClick={() => mudarStatus('encerrada')}><i className="bi bi-stop-fill me-1"></i>Encerrar</button>
              </>
            )}
            {isEncerrada && (
              <button className="btn btn-sm btn-outline-success" onClick={() => mudarStatus('ativa')}><i className="bi bi-arrow-counterclockwise me-1"></i>Reabrir</button>
            )}
            <button
              className={`btn btn-sm ${campanha.visivel_gerente ? 'btn-outline-success' : 'btn-outline-secondary'}`}
              onClick={toggleVisivelGerente}
              title={campanha.visivel_gerente ? 'Os gerentes de loja enxergam esta campanha. Clique para ocultar.' : 'Esta campanha está oculta dos gerentes de loja. Clique para liberar.'}>
              <i className={`bi ${campanha.visivel_gerente ? 'bi-eye' : 'bi-eye-slash'} me-1`}></i>
              {campanha.visivel_gerente ? 'Gerentes veem' : 'Oculta p/ gerentes'}
            </button>
          </div>
        )}
      </div>

      {/* Tracker de fase da campanha */}
      <div className="mb-3 p-3 rounded" style={{ background: '#fff', border: '1px solid #EEE' }}>
        <CampanhaTracker
          campanha={campanha}
          qtdProdutos={produtos.length}
          qtdAprovados={produtos.filter(p => aprovados[p.id]).length}
          variant="header"
        />
      </div>

      {/* Header — linha 2: barra de ações */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 p-2 rounded" style={{ backgroundColor: '#FAFAFA', border: '1px solid #EEE' }}>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          {/* Menu de Ações — junta as ferramentas da barra num só botão */}
          {podeEditar && ((podeAdicionar && temPermissao('produtos_adicionar')) || podeDiretoria) && (
            <div className="position-relative">
              <button className="btn-primary-mkt" onClick={() => setMenuAcoesOpen(o => !o)}>
                <i className="bi bi-list-ul me-1"></i>Ações <i className="bi bi-chevron-down ms-1" style={{ fontSize: '0.7rem' }}></i>
              </button>
              {menuAcoesOpen && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }} onClick={() => setMenuAcoesOpen(false)} />
                  <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: '#fff', border: '1px solid #e2e2e2', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', zIndex: 1001, minWidth: 230, overflow: 'hidden' }}>
                    {podeAdicionar && temPermissao('produtos_adicionar') && (
                      <button className="acao-item" onClick={() => { setMenuAcoesOpen(false); setShowBusca(true); }}><i className="bi bi-plus-lg me-2"></i>Adicionar Produtos</button>
                    )}
                    <button className="acao-item" onClick={() => { setMenuAcoesOpen(false); navigate(linkEstudio(id)); }}><i className="bi bi-palette-fill me-2"></i>Abrir no Estúdio de Criação</button>
                    <button className="acao-item" onClick={() => { setMenuAcoesOpen(false); navigate(linkEstudio(id, 'catalogo')); }}><i className="bi bi-journal-richtext me-2"></i>Montar catálogo (PDF)</button>
                    <button className="acao-item" onClick={() => { setMenuAcoesOpen(false); navigate(linkEstudio(id, 'poster')); }}><i className="bi bi-file-earmark-image me-2"></i>Montar pôster A3</button>
                    <button className="acao-item" onClick={() => { setMenuAcoesOpen(false); navigate(linkEstudio(id, 'stories')); }}><i className="bi bi-instagram me-2"></i>Montar stories do Instagram</button>
                    {podeEditar && temPermissao('produtos_adicionar') && (
                      <button className="acao-item" onClick={async () => { setMenuAcoesOpen(false); await atualizarPrecosSistema(); await carregarEstoque(); }}><i className="bi bi-arrow-repeat me-2"></i>Atualizar Dados</button>
                    )}
                    {podeEditar && podeDiretoria && !modoPrazo && (
                      <button className="acao-item" onClick={() => { setMenuAcoesOpen(false); setShowLote(true); }}><i className="bi bi-percent me-2"></i>Desconto em Lote</button>
                    )}
                    {podeEditar && podeDiretoria && modoPrazo && (
                      <button className="acao-item" onClick={() => { setMenuAcoesOpen(false); acaoPrazoSistema(); }}><i className="bi bi-magic me-2"></i>Preço a prazo do sistema{taxaPrazoSistema != null ? ` (+${fmt(taxaPrazoSistema)}%)` : ''}</button>
                    )}
                    {podeEditar && podeDiretoria && tem12x && (
                      <button className="acao-item" onClick={() => { setMenuAcoesOpen(false); aplicar12xPadrao(); }}><i className="bi bi-3-circle me-2"></i>Aplicar 12x (à vista × 1,2)</button>
                    )}
                    {podeEditar && podeDiretoria && produtos.length > 0 && (
                      <button className="acao-item" onClick={() => { setMenuAcoesOpen(false); alinharComOutrasListas(); }}><i className="bi bi-files me-2"></i>Alinhar com outras listas</button>
                    )}
                    {podeAdicionar && temPermissao('produtos_adicionar') && produtos.length > 0 && (
                      <button className="acao-item text-danger" onClick={() => { setMenuAcoesOpen(false); setRemDisponibs([]); setRemClasses([]); setRemSemEstoque(false); setRemExcluidos([]); setShowRemoverLote(true); }}><i className="bi bi-trash me-2"></i>Remover em Lote</button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Tipo de precificação da campanha (Padrão / a partir do preço a prazo) */}
          {veDiretoria && (() => {
            const podeTrocar = podeEditar && (podeDiretoria || temPermissao('campanhas_editar'));
            const rotulo = modoPrazo ? `A prazo − ${percCampanha != null ? fmt(percCampanha) : '—'}%` : 'Padrão';
            return (
              <Tooltip texto={modoPrazo
                ? 'Preço = preço a prazo × (1 − %). Clique para mudar o % ou voltar ao padrão.'
                : 'O Preço é digitado direto. Clique para precificar a partir do preço a prazo.'} largura={250}>
                <button className="btn btn-sm btn-outline-secondary" onClick={podeTrocar ? editarPrecificacao : undefined}
                  disabled={!podeTrocar} style={{ fontSize: '0.8rem', ...(modoPrazo ? { borderColor: 'var(--mostarda)', color: 'var(--mostarda)' } : {}) }}>
                  <i className="bi bi-calculator me-1"></i>Precificação: <b>{rotulo}</b>
                </button>
              </Tooltip>
            );
          })()}

          {/* Replicação automática ligada no aviso ("replicar sempre") — clique desliga */}
          {replicarSempre && (
            <Tooltip texto="Ao mudar o preço de um produto, replica sozinho nas outras listas abertas do mesmo período. Clique para voltar a perguntar." largura={250}>
              <button className="btn btn-sm" onClick={() => { setReplicarSempre(false); replicarSempreRef.current = false; }}
                style={{ fontSize: '0.78rem', background: '#F5EBF2', color: 'var(--mostarda)', border: '1px solid var(--mostarda)' }}>
                <i className="bi bi-files me-1"></i>Replicando nas outras listas <i className="bi bi-x-lg ms-1"></i>
              </button>
            </Tooltip>
          )}

          {/* Indicador de auto-save (substitui o botão "Salvar" manual) */}
          {podeEditar && (podeDiretoria || (veSugestoes && temPermissao('sugestoes_preencher'))) && (() => {
            const saving = salvando || autoSaveStatus === 'saving';
            const st = saving ? { txt: 'Salvando…', cor: '#8a6d00', bg: '#fff8e1', bd: '#ffe08a', spin: true }
              : autoSaveStatus === 'erro' ? { txt: 'Erro — salvar agora', ic: 'bi-exclamation-triangle-fill', cor: '#b23b3b', bg: '#fdeeee', bd: '#f2c4c4' }
              : autoSaveStatus === 'saved' ? { txt: 'Salvo', ic: 'bi-check-circle-fill', cor: '#1f7a3d', bg: '#e9f6ee', bd: '#bfe3cc' }
              : { txt: 'Salva automático', ic: 'bi-cloud-check', cor: '#666', bg: '#f2f2f2', bd: '#e2e2e2' };
            return (
              <button onClick={() => { if (podeDiretoria) salvarTudo(); else salvarSugestoes(); }} disabled={saving}
                title="As alterações são salvas sozinhas ~1s depois de você parar. Clique pra salvar agora."
                className="btn btn-sm d-inline-flex align-items-center gap-1"
                style={{ color: st.cor, background: st.bg, border: `1px solid ${st.bd}`, fontWeight: 600, fontSize: '0.82rem' }}>
                {st.spin ? <span className="spinner-border spinner-border-sm" style={{ width: 13, height: 13 }}></span> : <i className={`bi ${st.ic}`}></i>}
                {st.txt}
              </button>
            );
          })()}
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {temPermissao('relatorio_comissao_extra') && (
            <button className="btn-outline-mkt" onClick={() => navigate(`/campanhas/${id}/comissao-extra`)}>
              <i className="bi bi-cash-coin me-1"></i>Comissão Extra
            </button>
          )}
          {isAtiva && temPermissao('enviar_erp') && (
            <button className="btn-primary-mkt" onClick={() => sincronizarERP()} disabled={enviandoERP || !produtos.some(p => aprovados[p.id])}>
              {enviandoERP ? <><span className="spinner-border spinner-border-sm me-1"></span>Sincronizando...</> : <><i className="bi bi-cloud-arrow-up me-1"></i>{campanha?.erp_oferta_oid ? 'Sincronizar com o ERP' : 'Criar oferta no ERP'}</>}
            </button>
          )}
        </div>
      </div>

      {isEncerrada && (
        <div className="alert alert-secondary py-2 mb-2 d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
          <i className="bi bi-lock-fill me-2"></i> Campanha encerrada — modo somente leitura
        </div>
      )}

      {/* Badge atualização + Filiais + Filtro */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {ultimaAtualizacao && <small style={{ color: '#888' }}><i className="bi bi-clock me-1"></i>Estoque: {ultimaAtualizacao.toLocaleTimeString('pt-BR')}</small>}
          {/* Filtro dinâmico por código/descrição */}
          <div className="position-relative">
            <i className="bi bi-search" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#999', fontSize: '0.8rem', pointerEvents: 'none' }}></i>
            <input type="text" className="form-control form-control-sm" style={{ width: 210, fontSize: '0.8rem', paddingLeft: 26 }}
              placeholder="Filtrar por código ou descrição..." value={busca} onChange={e => setBusca(e.target.value)} />
            {busca && <i className="bi bi-x-circle-fill" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#bbb', fontSize: '0.8rem', cursor: 'pointer' }} onClick={() => setBusca('')}></i>}
          </div>
          <select className="form-select form-select-sm" style={{ width: 'auto', fontSize: '0.8rem' }} value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
            <option value="todos">Todos ({produtos.length})</option>
            <option value="aprovados">Aprovados ({produtos.filter(p => aprovados[p.id]).length})</option>
            <option value="pendente_aprovacao">Pendente de aprovação ({produtos.filter(p => !aprovados[p.id]).length})</option>
            <option value="preco_definido">Preço definido, aguardando aprovação ({produtos.filter(p => precosDiretoria[p.id] && !aprovados[p.id]).length})</option>
            <option value="sem_preco">Sem preço ({produtos.filter(p => !precosDiretoria[p.id]).length})</option>
            <option value="critico">⚠️ Críticos (MKP negativo / vai faltar / estoque crítico)</option>
            <option value="avisos">Com algum aviso</option>
            {veDiretoria && <option value="erp_pendente">🔴 Pendência p/ o ERP ({produtos.filter(p => !getValidacoesErp(p, getEstoque(p.codpro)).ok).length})</option>}
          </select>
          <small style={{ color: '#888' }}>{produtosFiltrados.length} de {produtos.length}</small>
          {(() => {
            // Linhas que já não casam no filtro mas continuam na tela (o usuário acabou de preenchê-las)
            const fora = produtosFiltrados.filter(p => !casaNoFiltro(p)).length;
            if (!fora) return null;
            return (
              <small style={{ color: '#8a6d3b' }}>
                <i className="bi bi-pin-angle-fill me-1"></i>
                {fora} {fora === 1 ? 'já não bate' : 'já não batem'} no filtro
                <button type="button" className="btn btn-link btn-sm p-0 ms-1" style={{ fontSize: '0.8rem', verticalAlign: 'baseline' }}
                  onClick={reaplicarFiltro}>atualizar lista</button>
              </small>
            );
          })()}
        </div>
        {false && temPermissao('campanhas_editar') && (
          <div className="d-flex align-items-center gap-1 flex-wrap">
          </div>
        )}
      </div>

      {/* Tabela principal — simplificada para diretoria */}
      <div className="card p-2">
        <div className="tabela-container tabela-sticky" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <table className="table table-mkt table-hover table-sm mb-0" style={{ fontSize: '0.8rem' }}>
            <thead>
              {/* Linha de grupos — DIRETORIA e GERENTES com cor de destaque para indicar onde cada papel preenche */}
              <tr style={{ background: 'var(--mostarda-escuro)' }}>
                <th colSpan="4" style={{ textAlign: 'center', fontSize: '0.7rem', letterSpacing: '0.5px', borderRight: '2px solid rgba(255,255,255,0.2)', padding: '4px 8px' }}>PRODUTO</th>
                <th colSpan="4" style={{ textAlign: 'center', fontSize: '0.7rem', letterSpacing: '0.5px', borderRight: '2px solid rgba(255,255,255,0.2)', padding: '4px 8px' }}>SISTEMA</th>
                {veSugestoes && (
                  <th colSpan="2" style={{ textAlign: 'center', fontSize: '0.7rem', letterSpacing: '0.5px', borderRight: '2px solid rgba(255,255,255,0.2)', padding: '4px 8px', background: 'var(--destaque-gerente)' }}>
                    {isGerente ? 'MINHA SUGESTÃO' : 'GERENTES'}
                  </th>
                )}
                {veDiretoria && (
                  <th colSpan={(tem12x ? 6 : 5) + (modoPrazo ? 2 : 0)} style={{ textAlign: 'center', fontSize: '0.7rem', letterSpacing: '0.5px', borderRight: '2px solid rgba(255,255,255,0.2)', padding: '4px 8px', background: 'var(--destaque-diretoria)' }}>DIRETORIA</th>
                )}
                {veDiretoria && <th style={{ textAlign: 'center', fontSize: '0.7rem', letterSpacing: '0.5px', borderRight: '2px solid rgba(255,255,255,0.2)', padding: '4px 8px' }}>ERP</th>}
                <th colSpan="2" style={{ textAlign: 'center', fontSize: '0.7rem', letterSpacing: '0.5px', padding: '4px 8px' }}></th>
              </tr>
              {/* Linha de colunas */}
              <tr>
                <th style={{ width: 60, textAlign: 'center', verticalAlign: 'middle' }}>Foto</th>
                <ThSort campo="codpro" style={{ minWidth: 70, borderRight: 'none' }}>Código</ThSort>
                <ThSort campo="descricao" style={{ minWidth: 180 }}>Descrição</ThSort>
                <ThSort campo="disponibilidade" style={{ borderRight: '2px solid rgba(255,255,255,0.2)' }}>Disponib.</ThSort>
                <ThSort campo="custo">Custo</ThSort>
                <ThSort campo="preco_sistema">À Vista</ThSort>
                <ThSort campo="mkp_sistema">MKP</ThSort>
                <ThSort campo="a_chegar" style={{ borderRight: '2px solid rgba(255,255,255,0.2)' }}>A chegar</ThSort>
                {veSugestoes && (isGerente ? (
                  <>
                    <th style={{ textAlign: 'center', verticalAlign: 'middle', background: 'var(--destaque-gerente)' }}>Preço</th>
                    <th style={{ textAlign: 'center', verticalAlign: 'middle', borderRight: '2px solid rgba(255,255,255,0.2)', background: 'var(--destaque-gerente)' }}>MKP</th>
                  </>
                ) : (
                  <>
                    <ThSort campo="media" style={{ background: 'var(--destaque-gerente)' }}>~Preço</ThSort>
                    <th style={{ textAlign: 'center', verticalAlign: 'middle', borderRight: '2px solid rgba(255,255,255,0.2)', background: 'var(--destaque-gerente)' }}>~MKP</th>
                  </>
                ))}
                {veDiretoria && modoPrazo && (
                  <ThSort campo="preco_prazo" style={{ background: 'var(--destaque-diretoria)' }}>
                    <Tooltip texto={`Vem preenchido com o preço de venda do ERP + ${taxaPrazoSistema != null ? fmt(taxaPrazoSistema) : '…'}% (forma de pagamento a prazo). Pode alterar.`} largura={250}>
                      <span style={{ borderBottom: '1px dotted rgba(255,255,255,0.6)' }}>A prazo</span>
                    </Tooltip>
                  </ThSort>
                )}
                {veDiretoria && modoPrazo && (
                  <th style={{ textAlign: 'center', verticalAlign: 'middle', background: 'var(--destaque-diretoria)' }}>
                    <Tooltip texto={`Desconto sobre o preço a prazo. Vazio = usa o % da campanha (${percCampanha != null ? fmt(percCampanha) : '—'}%).`} largura={240}>
                      <span style={{ borderBottom: '1px dotted rgba(255,255,255,0.6)' }}>%</span>
                    </Tooltip>
                  </th>
                )}
                {veDiretoria && (
                  <th style={{ textAlign: 'center', verticalAlign: 'middle', background: 'var(--destaque-diretoria)' }}>
                    {modoPrazo ? (
                      <Tooltip texto={`Calculado: preço a prazo × (1 − %)${faixasArred.length ? ', com arredondamento dos centavos' : ''}. Pode alterar à mão (fica destacado). É este o preço que vai pro ERP e pras plaquinhas.`} largura={260}>
                        <span style={{ borderBottom: '1px dotted rgba(255,255,255,0.6)' }}>Preço</span>
                      </Tooltip>
                    ) : 'Preço'}
                  </th>
                )}
                {veDiretoria && <th style={{ textAlign: 'center', verticalAlign: 'middle', background: 'var(--destaque-diretoria)' }}>MKP</th>}
                {veDiretoria && tem12x && <th style={{ textAlign: 'center', verticalAlign: 'middle', background: 'var(--destaque-diretoria)' }}>12x</th>}
                {veDiretoria && <ThSort campo="comissao" style={{ background: 'var(--destaque-diretoria)' }}>Com.</ThSort>}
                {veDiretoria && (
                  <th style={{ textAlign: 'center', verticalAlign: 'middle', background: 'var(--destaque-diretoria)' }}>
                    <Tooltip texto="Quantidade enviada ao ERP. Vazio = usa o estoque disponível (mínimo 1)." largura={240}>
                      <span style={{ whiteSpace: 'nowrap', borderBottom: '1px dotted rgba(255,255,255,0.6)' }}>Qtd ofert.</span>
                    </Tooltip>
                  </th>
                )}
                {veDiretoria && (
                  <th style={{ textAlign: 'center', verticalAlign: 'middle', borderRight: '2px solid rgba(255,255,255,0.2)', background: 'var(--destaque-diretoria)' }}>
                    {podeDiretoria ? (
                      <label className="d-flex align-items-center justify-content-center gap-1" style={{ cursor: 'pointer', whiteSpace: 'nowrap', margin: 0 }}>
                        <input type="checkbox" checked={produtos.length > 0 && produtos.every(p => aprovados[p.id])}
                          onChange={e => { const val = e.target.checked; setAprovados(prev => { const n = { ...prev }; produtos.forEach(p => { n[p.id] = val; }); return n; }); }} />
                        <i className="bi bi-check-circle"></i>
                      </label>
                    ) : <i className="bi bi-check-circle"></i>}
                  </th>
                )}
                {veDiretoria && (
                  <th style={{ textAlign: 'center', verticalAlign: 'middle', borderRight: '2px solid rgba(255,255,255,0.2)' }}>
                    <Tooltip texto={campanha?.erp_oferta_oid ? 'Validações da ATUALIZAÇÃO da oferta no ERP (oferta já criada): preço à vista < sistema, preço 12x e quantidade não menor que o já vendido.' : 'Validações da CRIAÇÃO da oferta no ERP: preço à vista < sistema e preço 12x.'} largura={300}>
                      <span style={{ whiteSpace: 'nowrap', borderBottom: '1px dotted rgba(255,255,255,0.6)' }}>Validação ERP</span>
                    </Tooltip>
                  </th>
                )}
                <ThSort campo="status">Status</ThSort>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {produtosFiltrados.map(p => {
                const est = getEstoque(p.codpro);
                const media = calcMedia(p.id);
                const status = getStatusBadge(p, est);
                const isExpanded = expandido === p.id;
                const minhaFilial = user?.filial_id;
                const sug = sugestoes.find(s => s.campanha_produto_id === p.id && String(s.filial_codigo) === String(minhaFilial));

                return (
                  <React.Fragment key={p.id}>
                    <tr className="linha-produto" style={{ cursor: 'pointer' }} onClick={() => setExpandido(isExpanded ? null : p.id)}>
                      <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <ImagemProduto codpro={p.codpro} tamanho={52} clicavel alt={p.descricao} />
                      </td>
                      <td className="fw-semibold">{p.codpro}</td>
                      <td title={p.descricao}>{descTela(p)}</td>
                      <td style={{ borderRight: '2px solid #eee' }}><small>{p.disponibilidade || '—'}</small></td>
                      <td>{fmt(p.custo)}</td>
                      <td>{fmt(p.preco_sistema)}</td>
                      <td>{fmtMkp(p.mkp_sistema)}</td>
                      {/* Comprado do fornecedor e ainda não entregue (pedido_de_compras_pendentes) — não entra no estoque das lojas */}
                      <td style={{ borderRight: '2px solid #eee', textAlign: 'center' }}>
                        {est?.qtd_a_chegar > 0 ? (
                          <Tooltip texto={`Comprado do fornecedor, ainda não chegou${est.previsao_chegada ? ` · previsão ${new Date(est.previsao_chegada).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}` : ''}. Clique para ver por filial.`}>
                            <span className="fw-semibold" style={{ color: '#6B2C5C', textDecoration: 'underline dotted', cursor: 'pointer' }} onClick={e => { e.stopPropagation(); abrirEstoqueFilial(p.codpro); }}>{fmt(est.qtd_a_chegar)}</span>
                          </Tooltip>
                        ) : <span className="text-muted">—</span>}
                      </td>

                      {/* Sugestões */}
                      {veSugestoes && (isGerente ? (
                        <>
                          <td style={{ borderLeft: '2px solid #eee' }} onClick={e => e.stopPropagation()}>
                            {temPermissao('sugestoes_preencher') ? (
                              <input type="number" step="0.01" className="form-control form-control-sm"
                                style={{ width: 80, fontSize: '0.78rem' }}
                                value={sugestoesLocal[p.id] ?? (sug?.preco_sugerido || '')}
                                onChange={e => setSugestoesLocal(prev => ({ ...prev, [p.id]: e.target.value }))}
                                disabled={isEncerrada} />
                            ) : <span>{sug ? fmt(sug.preco_sugerido) : '—'}</span>}
                          </td>
                          <td style={{ borderRight: '2px solid #eee' }}>{(() => {
                            const precoSug = parseFloat(sugestoesLocal[p.id] ?? sug?.preco_sugerido);
                            const custo = parseFloat(p.custo);
                            return precoSug && custo > 0 ? fmtMkp((precoSug - custo) / custo) : '—';
                          })()}</td>
                        </>
                      ) : (
                        <>
                          <td style={{ fontWeight: 600, cursor: media.count ? 'pointer' : 'default' }}
                            onClick={e => { e.stopPropagation(); if (media.count) setShowSugestoes(p.id); }}>
                            {media.preco ? (
                              <Tooltip texto={media.count ? `${media.count} sugestões — clique para ver detalhes por filial` : ''}>
                                <span onClick={e => { e.stopPropagation(); if (media.count) setShowSugestoes(p.id); }}>
                                  <span style={{ textDecoration: 'underline dotted' }}>{fmt(media.preco)}</span> <small style={{ color: '#999' }}>({media.count})</small>
                                </span>
                              </Tooltip>
                            ) : '—'}
                          </td>
                          <td style={{ fontWeight: 600, borderRight: '2px solid #eee' }}>{media.mkp ? fmtMkp(media.mkp) : '—'}</td>
                        </>
                      ))}

                      {/* Diretoria (editável) ou somente leitura (marketing/gestor) */}
                      {veDiretoria && modoPrazo && (
                        <td style={{ borderLeft: '2px solid #eee' }} onClick={e => e.stopPropagation()}>
                          {podeDiretoria ? (
                            <input type="text" inputMode="numeric" className="form-control form-control-sm"
                              style={{ width: 85, fontSize: '0.78rem' }} placeholder="0,00"
                              value={precosPrazo[p.id] || ''}
                              onChange={e => handlePrazo(p.id, maskMoeda(e.target.value))}
                              onFocus={() => focoPreco(p.id, 'prazo')}
                              onBlur={() => saidaPreco(p)}
                              disabled={isEncerrada} />
                          ) : <span>{precosPrazo[p.id] ? fmt(numPrazo(p.id)) : '—'}</span>}
                        </td>
                      )}
                      {veDiretoria && modoPrazo && (
                        <td onClick={e => e.stopPropagation()}>
                          {podeDiretoria ? (
                            <input type="text" inputMode="numeric" className="form-control form-control-sm"
                              style={{ width: 62, fontSize: '0.78rem', ...(temPercProprio(p.id) ? { borderColor: 'var(--mostarda)', fontWeight: 600 } : {}) }}
                              placeholder={percCampanha != null ? fmt(percCampanha) : '0,00'}
                              title={temPercProprio(p.id) ? '% próprio deste produto (apague para voltar ao da campanha)' : '% da campanha'}
                              value={percsPrazo[p.id] || ''}
                              onChange={e => handlePercProd(p.id, maskMoeda(e.target.value))}
                              onFocus={() => focoPreco(p.id, 'perc')}
                              onBlur={() => saidaPreco(p)}
                              disabled={isEncerrada} />
                          ) : <span className={temPercProprio(p.id) ? 'fw-bold' : 'text-muted'}>{fmt(percEfetivo(p.id))}%</span>}
                        </td>
                      )}
                      {veDiretoria && modoPrazo && (() => {
                        // Preço calculado (prazo − % + arredondamento), mas a diretoria pode sobrescrever.
                        const manual = ehPrecoManual(p.id);
                        const calc = numPrazo(p.id) > 0 ? precoCalculado(numPrazo(p.id), percEfetivo(p.id)) : null;
                        return (
                          <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                            {podeDiretoria ? (
                              <>
                                <input type="text" inputMode="numeric" className="form-control form-control-sm d-inline-block"
                                  style={{ width: 80, fontSize: '0.78rem', fontWeight: 700, ...(manual ? { borderColor: '#e08a00', background: '#fff8e1' } : {}) }}
                                  placeholder="0,00"
                                  value={precosDiretoria[p.id] || ''}
                                  onChange={e => handlePrecoDir(p.id, maskMoeda(e.target.value))}
                                  onFocus={() => focoPreco(p.id, 'dir')}
                                  onBlur={() => saidaPreco(p)}
                                  disabled={isEncerrada} />
                                {manual && calc != null && !isEncerrada && (
                                  <Tooltip texto={`Preço digitado à mão. Pelo prazo e % seria ${fmt(calc)}. Clique para voltar ao calculado.`} largura={240}>
                                    <button type="button" className="btn btn-link btn-sm p-0 ms-1" style={{ color: '#e08a00', verticalAlign: 'middle' }}
                                      onClick={() => recalcularDoPrazo(p.id, numPrazo(p.id), percEfetivo(p.id))}>
                                      <i className="bi bi-arrow-counterclockwise"></i>
                                    </button>
                                  </Tooltip>
                                )}
                              </>
                            ) : (
                              <span className="fw-bold">{precosDiretoria[p.id] ? fmt(numDir(p.id)) : '—'}{manual && <i className="bi bi-pencil-fill ms-1" style={{ color: '#e08a00', fontSize: '0.65rem' }} title="Preço digitado à mão"></i>}</span>
                            )}
                          </td>
                        );
                      })()}
                      {veDiretoria && !modoPrazo && (
                        <td style={{ borderLeft: '2px solid #eee' }} onClick={e => e.stopPropagation()}>
                          {podeDiretoria ? (
                            <input type="text" inputMode="numeric" className="form-control form-control-sm"
                              style={{ width: 85, fontSize: '0.78rem' }} placeholder="0,00"
                              value={precosDiretoria[p.id] || ''}
                              onChange={e => handlePrecoDir(p.id, maskMoeda(e.target.value))}
                              onFocus={() => focoPreco(p.id, 'dir')}
                              onBlur={() => saidaPreco(p)}
                              disabled={isEncerrada} />
                          ) : <span className="fw-bold">{precosDiretoria[p.id] ? fmt(numDir(p.id)) : '—'}</span>}
                        </td>
                      )}
                      {veDiretoria && (
                        <td>{precosDiretoria[p.id] ? fmtMkp(parseFloat(p.custo) > 0 ? ((numDir(p.id) - parseFloat(p.custo)) / parseFloat(p.custo)) : 0) : '—'}</td>
                      )}
                      {veDiretoria && tem12x && (
                        <td onClick={e => e.stopPropagation()}>
                          {podeDiretoria ? (
                            <input type="text" inputMode="numeric" className="form-control form-control-sm"
                              style={{ width: 85, fontSize: '0.78rem' }} placeholder="0,00"
                              value={precos12x[p.id] || ''}
                              onChange={e => setPrecos12x(prev => ({ ...prev, [p.id]: maskMoeda(e.target.value) }))}
                              onFocus={() => focoPreco(p.id, '12x')}
                              onBlur={() => saidaPreco(p)}
                              disabled={isEncerrada} />
                          ) : <span>{precos12x[p.id] ? fmt(num12x(p.id)) : '—'}</span>}
                        </td>
                      )}
                      {veDiretoria && (
                        <td onClick={e => e.stopPropagation()}>
                          {podeDiretoria ? (
                            <input type="text" inputMode="numeric" className="form-control form-control-sm"
                              style={{ width: 70, fontSize: '0.78rem' }} placeholder="0,00"
                              value={comissoes[p.id] || ''}
                              onChange={e => setComissoes(prev => ({ ...prev, [p.id]: maskMoeda(e.target.value) }))}
                              onFocus={() => focoPreco(p.id, 'com')}
                              onBlur={() => saidaPreco(p)}
                              disabled={isEncerrada} />
                          ) : <span>{comissoes[p.id] ? `R$ ${fmt(numCom(p.id))}` : '—'}</span>}
                        </td>
                      )}
                      {veDiretoria && (() => {
                        const vm = parseFloat(est?.venda_minima) || 0;
                        const stQ = erpStatus[p.codpro?.trim()];
                        const vend = Math.max(stQ?.avista?.vendida || 0, stQ?.doze?.vendida || 0);
                        return (
                          <td onClick={e => e.stopPropagation()}>
                            {podeDiretoria ? (
                              <input type="text" inputMode="numeric" className="form-control form-control-sm"
                                style={{ width: 72, fontSize: '0.78rem' }}
                                placeholder={est?.estoque_disp != null ? numeroParaMoeda(Math.max(0, est.estoque_disp)) : 'estoque'}
                                value={qtdOfertadas[p.id] ?? ''}
                                onChange={e => setQtdOfertadas(prev => ({ ...prev, [p.id]: maskMoeda(e.target.value) }))}
                                onFocus={() => setCampoEmEdicao(`${p.id}:qtd`)}
                                onBlur={() => { setCampoEmEdicao(null); ajustarQtdMultiplo(p.id, p.codpro); }}
                                disabled={isEncerrada} />
                            ) : <span>{qtdOfertadas[p.id] ? qtdOfertadas[p.id] : (est?.estoque_disp != null ? numeroParaMoeda(Math.max(0, est.estoque_disp)) : '—')}</span>}
                            {(vm > 0 || vend > 0) && (
                              <div style={{ fontSize: '0.62rem', color: '#888', lineHeight: 1.15, marginTop: 2, whiteSpace: 'nowrap' }}>
                                {vm > 0 && <Tooltip texto={`A quantidade tem que ser múltiplo de ${round2(vm)} (venda mínima do produto)`}><span style={{ borderBottom: '1px dotted #bbb' }}>múlt. {round2(vm)}</span></Tooltip>}
                                {vend > 0 && <Tooltip texto={`Já vendido nesta oferta: ${round2(vend)}. A quantidade ofertada não pode ser menor que isso.`}><span className="ms-1" style={{ color: '#c82333', borderBottom: '1px dotted #e0a0a0' }}><i className="bi bi-cart-check"></i> {round2(vend)}</span></Tooltip>}
                              </div>
                            )}
                          </td>
                        );
                      })()}
                      {veDiretoria && (
                        <td style={{ borderRight: '2px solid #eee' }} onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={aprovados[p.id] || false}
                            onChange={e => podeDiretoria && setAprovados(prev => ({ ...prev, [p.id]: e.target.checked }))}
                            disabled={isEncerrada || !podeDiretoria} />
                        </td>
                      )}

                      {veDiretoria && (() => {
                        const v = getValidacoesErp(p, est);
                        const naoAprovado = !aprovados[p.id];
                        return (
                          <td style={{ borderRight: '2px solid #eee', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                            <Tooltip largura={320} texto={
                              <span>
                                <b>{v.cenario === 'atualizar' ? 'Atualização da oferta existente' : 'Criação da oferta'}</b>
                                {naoAprovado && <><br />⚪ Produto não aprovado — só vai ao ERP depois de aprovado</>}
                                {v.itens.map((it, i) => <React.Fragment key={i}><br />{it.nivel === 'bloqueio' ? '⛔' : it.nivel === 'info' ? 'ℹ️' : '✅'} {it.msg}</React.Fragment>)}
                              </span>
                            }>
                              <span className="badge" style={{ backgroundColor: v.ok ? '#28a745' : '#c82333', fontSize: '0.68rem', padding: '3px 7px', cursor: 'default', whiteSpace: 'nowrap' }}>
                                <i className={`bi ${v.ok ? 'bi-check-circle' : 'bi-exclamation-triangle-fill'} me-1`}></i>
                                {v.ok ? 'OK' : `${v.bloqueios.length} pend.`}
                              </span>
                            </Tooltip>
                          </td>
                        );
                      })()}

                      <td>
                        <div className="d-flex align-items-center flex-wrap gap-1">
                          <Tooltip texto="Clique para ver histórico">
                            <span className={status.cls} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={e => { e.stopPropagation(); abrirHistorico(p.id); }}>
                              <i className={`bi ${status.icon} me-1`}></i>{status.label}
                            </span>
                          </Tooltip>
                          {getAvisos(p, est).map(av => (
                            <Tooltip key={av.chave} texto={av.msg} largura={280}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: av.cor, color: '#fff', fontSize: '0.75rem' }}>
                                <i className={`bi ${av.icon}`}></i>
                              </span>
                            </Tooltip>
                          ))}
                        </div>
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="d-flex gap-1 align-items-center">
                          {/* Badge de sincronização + vendida (do ERP) */}
                          {p.erp_sincronizado_em && (() => {
                            const st = erpStatus[p.codpro?.trim()];
                            const vend = st?.avista?.vendida;
                            const ofe = st?.avista?.ofertada;
                            return (
                              <Tooltip texto={st ? `No ERP — ofertada: ${ofe ?? '?'} · vendida: ${vend ?? 0}` : 'Sincronizado com o ERP'}>
                                <span className="badge" style={{ backgroundColor: '#28a745', fontSize: '0.68rem', padding: '3px 6px' }}>
                                  <i className="bi bi-cloud-check me-1"></i>{vend != null ? `${Math.round(vend)} vend.` : 'ERP'}
                                </span>
                              </Tooltip>
                            );
                          })()}
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => abrirHistorico(p.id)} title="Histórico"><i className="bi bi-clock-history"></i></button>
                          {/* Sincronizar só este produto (oferta já existe) */}
                          {isAtiva && campanha?.erp_oferta_oid && temPermissao('enviar_erp') && (
                            <button className="btn btn-sm btn-outline-primary" style={{ borderColor: '#6B2C5C', color: '#6B2C5C' }} disabled={sincronizandoUm === p.id || !aprovados[p.id]} onClick={() => sincronizarERP(p.id)} title="Sincronizar este produto com o ERP">
                              {sincronizandoUm === p.id ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-cloud-arrow-up"></i>}
                            </button>
                          )}
                          {/* Encerrar (igualar ofertada=vendida) — só se já sincronizado */}
                          {isAtiva && p.erp_sincronizado_em && temPermissao('enviar_erp') && (
                            <button className="btn btn-sm btn-outline-warning" disabled={sincronizandoUm === p.id} onClick={() => encerrarProduto(p.id, p.descricao)} title="Encerrar no ERP (igualar ofertada à vendida)">
                              <i className="bi bi-stop-circle"></i>
                            </button>
                          )}
                          {/* Remover — só enquanto NÃO sincronizado */}
                          {podeAdicionar && !p.erp_sincronizado_em && temPermissao('produtos_adicionar') && (
                            <button className="btn btn-sm btn-outline-danger" onClick={() => removerProduto(p.id, p.descricao)} title="Remover"><i className="bi bi-x"></i></button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Accordion estoque — só para diretoria/marketing/gestor */}
                    {!isGerente && isExpanded && est && (() => {
                      const codpro = p.codpro?.trim();
                      const vendasProd = vendasAnoAnterior?.por_codpro?.[codpro];
                      const totalPeriodo = vendasProd ? vendasProd.mesmo_periodo.reduce((a, v) => a + v.qtd, 0) : 0;
                      const totalAno = vendasProd ? vendasProd.ano_inteiro.qtd : 0;
                      const anoRef = vendasAnoAnterior?.ano_anterior;
                      return (
                        <tr><td colSpan={20} className="p-0">
                          <div className="estoque-accordion">
                            <div className="row g-3 align-items-end">
                              <Tooltip texto="Ver estoque por filial">
                                <div className="col-auto" style={{ cursor: 'pointer' }} onClick={() => abrirEstoqueFilial(p.codpro)}>
                                  <small className="text-muted">Estoque Disponível <i className="bi bi-search ms-1"></i></small>
                                  <div className="fw-bold" style={{ color: '#6B2C5C', textDecoration: 'underline dotted' }}>{fmt(est.estoque_disp)}</div>
                                </div>
                              </Tooltip>
                              <Tooltip texto="Ver pedidos a chegar por filial">
                                <div className="col-auto" style={{ cursor: 'pointer' }} onClick={() => abrirEstoqueFilial(p.codpro)}>
                                  <small className="text-muted">Qtd a Chegar <i className="bi bi-search ms-1"></i></small>
                                  <div className="fw-bold" style={{ color: '#6B2C5C', textDecoration: 'underline dotted' }}>{fmt(est.qtd_a_chegar)}</div>
                                </div>
                              </Tooltip>
                              <div className="col-auto"><small className="text-muted">Previsão Chegada</small><div className="fw-bold">{est.previsao_chegada ? new Date(est.previsao_chegada).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'}</div></div>
                              <div className="col-auto"><small className="text-muted">Custo Atual</small><div className="fw-bold">R$ {fmt(est.custo)}</div></div>
                              <div className="col-auto"><small className="text-muted">Venda Atual</small><div className="fw-bold">R$ {fmt(est.venda)}</div></div>
                              <Tooltip texto={vendasProd ? 'Ver vendas por filial' : 'Carregando...'}>
                                <div className="col-auto" style={{ borderLeft: '1px solid #ddd', paddingLeft: 16, cursor: vendasProd ? 'pointer' : 'default' }}
                                     onClick={() => vendasProd && setShowVendasFilial({ codpro, escopo: 'periodo' })}>
                                  <small className="text-muted">Vendeu mesmo período {anoRef ? `${anoRef}` : ''} <i className="bi bi-search ms-1"></i></small>
                                  <div className="fw-bold" style={{ color: '#6B2C5C', textDecoration: vendasProd ? 'underline dotted' : 'none' }}>
                                    {vendasProd ? `${totalPeriodo.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} un` : '...'}
                                  </div>
                                </div>
                              </Tooltip>
                              <Tooltip texto={vendasProd ? 'Ver detalhe' : 'Carregando...'}>
                                <div className="col-auto" style={{ cursor: vendasProd ? 'pointer' : 'default' }}
                                     onClick={() => vendasProd && setShowVendasFilial({ codpro, escopo: 'ano' })}>
                                  <small className="text-muted">Vendeu ano todo {anoRef ? `${anoRef}` : ''} <i className="bi bi-search ms-1"></i></small>
                                  <div className="fw-bold" style={{ color: '#6B2C5C', textDecoration: vendasProd ? 'underline dotted' : 'none' }}>
                                    {vendasProd ? `${totalAno.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} un` : '...'}
                                  </div>
                                </div>
                              </Tooltip>
                            </div>
                          </div>
                        </td></tr>
                      );
                    })()}
                    {!isGerente && isExpanded && !est && (
                      <tr><td colSpan={20} className="p-0"><div className="estoque-accordion text-muted">Carregando estoque...</div></td></tr>
                    )}
                  </React.Fragment>
                );
              })}
              {produtosFiltrados.length === 0 && (
                <tr><td colSpan={20} className="text-center text-muted py-4">
                  {produtos.length === 0 ? 'Nenhum produto adicionado. Clique em "Adicionar Produtos" para começar.' : 'Nenhum produto encontrado com esse filtro.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ==================== MODAIS ==================== */}

      {/* Modal Sugestões por Filial */}
      {showSugestoes && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card p-4" style={{ width: '90%', maxWidth: 600 }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0">Sugestões por Filial</h6>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowSugestoes(null)}><i className="bi bi-x-lg"></i></button>
            </div>
            {(() => {
              const prod = produtos.find(p => p.id === showSugestoes);
              const mediaInfo = calcMedia(showSugestoes);
              return prod ? (
                <div className="mb-3 p-2" style={{ background: '#f8f6f0', borderRadius: 8, fontSize: '0.85rem' }}>
                  <strong>{prod.codpro}</strong> — {descTela(prod)}
                  {mediaInfo.preco && <span className="ms-3" style={{ color: '#6B2C5C' }}>Média: <strong>{fmt(mediaInfo.preco)}</strong> (MKP {fmtMkp(mediaInfo.mkp)})</span>}
                </div>
              ) : null;
            })()}
            <table className="table table-sm table-hover mb-0">
              <thead style={{ background: '#f0f0f0' }}>
                <tr><th>Filial</th><th style={{ textAlign: 'right' }}>Preço Sugerido</th><th style={{ textAlign: 'right' }}>MKP</th><th>Preenchido por</th></tr>
              </thead>
              <tbody>
                {filiais.map(f => {
                  const sug = sugestoes.find(s => s.campanha_produto_id === showSugestoes && s.filial_codigo === parseInt(f.codigo));
                  return (
                    <tr key={f.codigo} style={{ opacity: sug ? 1 : 0.5 }}>
                      <td className="fw-semibold">{f.nome}</td>
                      <td style={{ textAlign: 'right' }}>{sug ? <strong>{fmt(sug.preco_sugerido)}</strong> : <span className="text-muted">—</span>}</td>
                      <td style={{ textAlign: 'right' }}>{sug ? fmtMkp(sug.mkp_sugerido) : '—'}</td>
                      <td style={{ fontSize: '0.8rem', color: '#888' }}>{sug?.usuario_nome || <span className="text-muted fst-italic">não preenchido</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Histórico — timeline de ações */}
      {showHistorico && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card p-4" style={{ width: '90%', maxWidth: 600, maxHeight: '80vh', overflow: 'auto' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0">Histórico do Produto</h6>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowHistorico(null)}><i className="bi bi-x-lg"></i></button>
            </div>
            {historico.length === 0 ? (
              <p className="text-muted text-center">Nenhum registro de histórico</p>
            ) : (
              <div style={{ borderLeft: '3px solid #6B2C5C', paddingLeft: 16 }}>
                {historico.map(h => (
                  <div key={h.id} style={{ marginBottom: 12, position: 'relative' }}>
                    <div style={{ position: 'absolute', left: -22, top: 4, width: 10, height: 10, borderRadius: '50%', background: '#6B2C5C' }} />
                    <div style={{ fontSize: '0.75rem', color: '#888' }}>
                      {h.criado_em ? new Date(h.criado_em).toLocaleString('pt-BR', { timeZone: 'UTC' }) : '—'} — {h.usuario_nome || 'Sistema'}
                    </div>
                    <div style={{ fontSize: '0.85rem' }}>
                      <span className="badge-filial me-1">{h.acao}</span>
                      {h.detalhes}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Vendas no Ano Anterior — detalhamento por filial */}
      {showVendasFilial && vendasAnoAnterior && (() => {
        const codpro = String(showVendasFilial.codpro).trim();
        const dados = vendasAnoAnterior.por_codpro[codpro] || { mesmo_periodo: [], ano_inteiro: { qtd: 0, valor_bruto: 0 } };
        const escopo = showVendasFilial.escopo;
        const prod = produtos.find(p => p.codpro?.trim() === codpro);
        const titulo = escopo === 'ano'
          ? `Vendas no ano inteiro de ${vendasAnoAnterior.ano_anterior}`
          : `Vendas no mesmo período (em ${vendasAnoAnterior.ano_anterior})`;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="card p-4" style={{ width: '90%', maxWidth: 700, maxHeight: '85vh', overflow: 'auto' }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0"><i className="bi bi-graph-up me-1"></i>{titulo}</h6>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowVendasFilial(null)}><i className="bi bi-x-lg"></i></button>
              </div>
              {prod && (
                <div className="mb-3 p-2" style={{ background: '#f8f6f0', borderRadius: 8, fontSize: '0.85rem' }}>
                  <strong>{prod.codpro}</strong> — {descTela(prod)}
                  {escopo === 'periodo' && (
                    <span className="ms-3 text-muted" style={{ fontSize: '0.78rem' }}>
                      {new Date(vendasAnoAnterior.periodo_referencia.inicio).toLocaleDateString('pt-BR', { timeZone: 'UTC' })} a {new Date(vendasAnoAnterior.periodo_referencia.fim).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                    </span>
                  )}
                </div>
              )}
              {escopo === 'ano' ? (
                <div className="card p-3" style={{ background: '#fafafa' }}>
                  <small className="text-muted">Total vendido em {vendasAnoAnterior.ano_anterior}</small>
                  <div className="fw-bold" style={{ fontSize: '1.4rem', color: '#6B2C5C' }}>
                    {dados.ano_inteiro.qtd.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} un
                  </div>
                  <small className="text-muted">R$ {fmt(dados.ano_inteiro.valor_bruto)} (valor bruto, todas as filiais)</small>
                </div>
              ) : dados.mesmo_periodo.length > 0 ? (
                <table className="table table-sm table-hover mb-2" style={{ fontSize: '0.85rem' }}>
                  <thead style={{ background: '#f0f0f0' }}>
                    <tr>
                      <th>Filial</th>
                      <th style={{ textAlign: 'right' }}>Qtd vendida</th>
                      <th style={{ textAlign: 'right' }}>Atend.</th>
                      <th style={{ textAlign: 'right' }}>Valor bruto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filiais.map(f => {
                      const cod = String(f.codigo).padStart(2, '0');
                      const v = dados.mesmo_periodo.find(x => x.filial === cod);
                      return (
                        <tr key={f.codigo} style={{ opacity: v ? 1 : 0.4 }}>
                          <td className="fw-semibold">{f.nome}</td>
                          <td style={{ textAlign: 'right' }} className="fw-bold">{v ? v.qtd.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '—'}</td>
                          <td style={{ textAlign: 'right' }}>{v ? v.atend : '—'}</td>
                          <td style={{ textAlign: 'right' }}>{v ? `R$ ${fmt(v.valor_bruto)}` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot style={{ background: '#f5ebf2', fontWeight: 600 }}>
                    <tr>
                      <td>TOTAL</td>
                      <td style={{ textAlign: 'right' }}>{dados.mesmo_periodo.reduce((a, v) => a + v.qtd, 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}</td>
                      <td style={{ textAlign: 'right' }}>{dados.mesmo_periodo.reduce((a, v) => a + v.atend, 0)}</td>
                      <td style={{ textAlign: 'right' }}>R$ {fmt(dados.mesmo_periodo.reduce((a, v) => a + v.valor_bruto, 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div className="text-muted text-center p-3">Sem vendas no mesmo período do ano anterior</div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Modal Estoque por Filial */}
      {showEstoqueFilial && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card p-4" style={{ width: '90%', maxWidth: 700, maxHeight: '85vh', overflow: 'auto' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0"><i className="bi bi-shop me-1"></i>Estoque e Pedidos por Filial</h6>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowEstoqueFilial(null)}><i className="bi bi-x-lg"></i></button>
            </div>
            {(() => {
              const prod = produtos.find(p => p.codpro === showEstoqueFilial);
              return prod && (
                <div className="mb-3 p-2" style={{ background: '#f8f6f0', borderRadius: 8, fontSize: '0.85rem' }}>
                  <strong>{prod.codpro}</strong> — {descTela(prod)}
                </div>
              );
            })()}
            {!estoquePorFilial && <div className="text-center text-muted"><span className="spinner-border spinner-border-sm me-2" />Carregando...</div>}
            {estoquePorFilial && (() => {
              const codpro = String(showEstoqueFilial).trim();
              const estLinhas = estoquePorFilial.estoque.filter(e => e.codpro === codpro);
              const pedLinhas = estoquePorFilial.pedidos.filter(p => p.codpro === codpro);
              const totalEst = estLinhas.reduce((a, e) => a + e.estoque_disp, 0);
              const totalPed = pedLinhas.reduce((a, p) => a + p.qtd_pendente, 0);
              return (
                <>
                  <table className="table table-sm table-hover mb-2" style={{ fontSize: '0.85rem' }}>
                    <thead style={{ background: '#f0f0f0' }}>
                      <tr>
                        <th>Filial</th>
                        <th style={{ textAlign: 'right' }}>Estoque Disp.</th>
                        <th style={{ textAlign: 'right' }} className="text-muted">Qtd</th>
                        <th style={{ textAlign: 'right' }} className="text-muted">Reservado</th>
                        <th style={{ textAlign: 'right' }}>A Chegar</th>
                        <th>Previsão</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filiais.map(f => {
                        const cod = String(f.codigo).padStart(2, '0');
                        const e = estLinhas.find(x => x.filial === cod);
                        const p = pedLinhas.find(x => x.filial === cod);
                        const semDado = !e && !p;
                        return (
                          <tr key={f.codigo} style={{ opacity: semDado ? 0.4 : 1 }}>
                            <td className="fw-semibold">{f.nome}</td>
                            <td style={{ textAlign: 'right' }} className={e && e.estoque_disp < 1 ? 'text-danger fw-bold' : 'fw-bold'}>
                              {e ? fmt(e.estoque_disp) : '—'}
                            </td>
                            <td style={{ textAlign: 'right' }} className="text-muted">{e ? fmt(e.quant) : '—'}</td>
                            <td style={{ textAlign: 'right' }} className="text-muted">{e && e.qtdereserv > 0 ? fmt(e.qtdereserv) : '—'}</td>
                            <td style={{ textAlign: 'right' }}>{p ? <span style={{ color: '#28a745', fontWeight: 600 }}>+{fmt(p.qtd_pendente)}</span> : '—'}</td>
                            <td style={{ fontSize: '0.78rem' }}>{p?.previsao_chegada ? new Date(p.previsao_chegada).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot style={{ background: '#f5ebf2', fontWeight: 600 }}>
                      <tr>
                        <td>TOTAL</td>
                        <td style={{ textAlign: 'right' }}>{fmt(totalEst)}</td>
                        <td className="text-muted"></td>
                        <td className="text-muted"></td>
                        <td style={{ textAlign: 'right', color: '#28a745' }}>{totalPed > 0 ? `+${fmt(totalPed)}` : '—'}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    <strong>Estoque Disp.</strong> = Qtd − Reservado. <strong>A Chegar</strong> = pedidos de compra pendentes.
                  </small>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Modal Busca Produtos */}
      {showBusca && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card p-4" style={{ width: '90%', maxWidth: 900, maxHeight: '85vh', overflow: 'auto' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">Adicionar Produtos</h5>
              <button className="btn btn-sm btn-outline-secondary" onClick={fecharBusca}><i className="bi bi-x-lg"></i></button>
            </div>

            {/* Adicionar rápido por código (Enter) */}
            <div className="mb-3 p-2 rounded" style={{ background: '#FAFAFA', border: '1px solid #EEE' }}>
              <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                <i className="bi bi-lightning-charge-fill me-1" style={{ color: 'var(--mostarda)' }}></i>Adicionar rápido por código
              </label>
              <div className="d-flex gap-2">
                <input className="form-control" value={codigoRapido} placeholder="Digite o código e tecle Enter"
                  onChange={e => setCodigoRapido(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCodigoRapido(); } }}
                  autoFocus />
                <button className="btn-outline-mkt" style={{ whiteSpace: 'nowrap' }} onClick={addCodigoRapido} disabled={buscandoCodigo}>
                  {buscandoCodigo ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-plus-lg me-1"></i>Adicionar</>}
                </button>
              </div>
              <small className="text-muted">Cada código vai pra lista abaixo. Você salva tudo de uma vez no final.</small>
              <div className="d-flex align-items-center flex-wrap gap-2 mt-2 pt-2" style={{ borderTop: '1px dashed #e2e2e2' }}>
                <small className="text-muted me-auto">
                  <i className="bi bi-file-earmark-spreadsheet me-1" style={{ color: '#1f7a3d' }}></i>
                  Ou importe uma planilha com os códigos na <b>1ª coluna</b> (.xlsx ou .csv)
                </small>
                <input ref={inputPlanilhaRef} type="file" accept=".xlsx,.csv,.txt" hidden
                  onChange={e => importarPlanilha(e.target.files?.[0])} />
                <button className="btn-outline-mkt" style={{ whiteSpace: 'nowrap' }}
                  onClick={() => inputPlanilhaRef.current?.click()} disabled={importandoPlanilha}>
                  {importandoPlanilha
                    ? <><span className="spinner-border spinner-border-sm me-1"></span>Importando...</>
                    : <><i className="bi bi-upload me-1"></i>Importar planilha</>}
                </button>
                <a href="/modelos/modelo-importar-produtos.xlsx" download="modelo-importar-produtos.xlsx"
                  className="small text-decoration-none" style={{ whiteSpace: 'nowrap', color: '#6B2C5C' }}>
                  <i className="bi bi-download me-1"></i>Baixar modelo
                </a>
              </div>
            </div>

            <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}><i className="bi bi-funnel me-1"></i>Ou busque por filtros:</div>
            <div className="row g-2 mb-3">
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: '0.85rem' }}>Código</label>
                <input className="form-control" value={buscaCodpro} onChange={e => setBuscaCodpro(e.target.value)} placeholder="Ex: 06015"
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); buscarProdutos(); } }} />
              </div>
              <div className="col-md-4" style={{ position: 'relative' }}>
                <label className="form-label" style={{ fontSize: '0.85rem' }}>Classes</label>
                <input className="form-control" placeholder="Clique ou digite..."
                  value={classeInput} onChange={e => { setClasseInput(e.target.value); setClasseDropOpen(true); }}
                  onFocus={() => setClasseDropOpen(true)} onBlur={() => setTimeout(() => setClasseDropOpen(false), 200)} />
                {classeDropOpen && (() => {
                  const termo = classeInput.trim().toLowerCase();
                  const filtradas = classes.filter(c => !buscaClasses.includes(c.clasprod) && (!termo || c.descricao.toLowerCase().includes(termo) || String(c.clasprod).toLowerCase().includes(termo)));
                  return (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, background: '#fff', border: '1px solid #ddd', borderRadius: '0 0 8px 8px', maxHeight: 260, overflowY: 'auto', zIndex: 99999, boxShadow: '0 6px 16px rgba(0,0,0,0.18)' }}>
                      {filtradas.length === 0
                        ? <div style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#999' }}>Nenhuma classe encontrada.</div>
                        : filtradas.slice(0, 50).map(c => (
                          <div key={c.clasprod} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f5f5f5' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f7f2f6'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                            onMouseDown={e => { e.preventDefault(); setBuscaClasses(prev => [...prev, c.clasprod]); setClasseInput(''); }}>{c.descricao} <small style={{ color: '#999' }}>({c.clasprod})</small></div>
                        ))}
                    </div>
                  );
                })()}
                {buscaClasses.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {buscaClasses.map(cl => (<span key={cl} className="badge-filial d-inline-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => setBuscaClasses(prev => prev.filter(c => c !== cl))}>{classes.find(c => c.clasprod === cl)?.descricao || cl} <i className="bi bi-x"></i></span>))}
                    <span style={{ fontSize: '0.72rem', color: '#999', cursor: 'pointer' }} onClick={() => setBuscaClasses([])}>limpar</span>
                  </div>
                )}
              </div>
              <div className="col-md-4" style={{ position: 'relative' }}>
                <label className="form-label" style={{ fontSize: '0.85rem' }}>Disponibilidade</label>
                <input className="form-control" placeholder="Clique ou digite..."
                  value={dispInput} onChange={e => { setDispInput(e.target.value); setDispDropOpen(true); }}
                  onFocus={() => setDispDropOpen(true)} onBlur={() => setTimeout(() => setDispDropOpen(false), 200)} />
                {dispDropOpen && (() => {
                  const termo = dispInput.trim().toLowerCase();
                  const filtradas = disponibilidades.filter(d => !buscaDisponibilidades.includes(d) && (!termo || d.toLowerCase().includes(termo)));
                  return (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, background: '#fff', border: '1px solid #ddd', borderRadius: '0 0 8px 8px', maxHeight: 260, overflowY: 'auto', zIndex: 99999, boxShadow: '0 6px 16px rgba(0,0,0,0.18)' }}>
                      {filtradas.length === 0
                        ? <div style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#999' }}>Nenhuma disponibilidade encontrada.</div>
                        : filtradas.map(d => (
                          <div key={d} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f5f5f5' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f7f2f6'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                            onMouseDown={e => { e.preventDefault(); setBuscaDisponibilidades(prev => [...prev, d]); setDispInput(''); }}>{d}</div>))}
                    </div>
                  );
                })()}
                {buscaDisponibilidades.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {buscaDisponibilidades.map(d => (<span key={d} className="badge-filial d-inline-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => setBuscaDisponibilidades(prev => prev.filter(x => x !== d))}>{d} <i className="bi bi-x"></i></span>))}
                    <span style={{ fontSize: '0.72rem', color: '#999', cursor: 'pointer' }} onClick={() => setBuscaDisponibilidades([])}>limpar</span>
                  </div>
                )}
              </div>
            </div>
            <div className="row g-2 mb-3">
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: '0.85rem' }}>Descrição</label>
                <input className="form-control" value={buscaDescricao} onChange={e => setBuscaDescricao(e.target.value)} placeholder="Ex: tubo"
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); buscarProdutos(); } }} />
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <div className="form-check mb-2" title="Traz só produtos com estoque disponível (quant - reservado) maior que zero em alguma filial">
                  <input className="form-check-input" type="checkbox" id="chk-com-estoque" checked={buscaComEstoque} onChange={e => setBuscaComEstoque(e.target.checked)} />
                  <label className="form-check-label" htmlFor="chk-com-estoque" style={{ fontSize: '0.85rem' }}>Só com <b>estoque &gt; 0</b></label>
                </div>
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <button className="btn-primary-mkt w-100" onClick={buscarProdutos} disabled={buscandoProdutos}>
                  {buscandoProdutos ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-search me-1"></i>Buscar</>}
                </button>
              </div>
            </div>
            {buscou && !buscandoProdutos && resultadosBusca.length === 0 && (
              <div className="text-center text-muted py-3" style={{ fontSize: '0.88rem', background: '#FAFAFA', border: '1px dashed #ddd', borderRadius: 8 }}>
                <i className="bi bi-search me-1"></i>Nenhum produto encontrado com esses filtros. Tente outra classe, disponibilidade ou descrição.
              </div>
            )}
            {resultadosBusca.length > 0 && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-1">
                  <small className="fw-semibold" style={{ color: '#6B2C5C' }}>{resultadosBusca.length} produto{resultadosBusca.length > 1 ? 's' : ''} encontrado{resultadosBusca.length > 1 ? 's' : ''} (agrupados por classe)</small>
                  <small className="text-muted">Marque os que quer e clique em "Jogar na lista"</small>
                </div>
                {resultadosBusca.length >= 150 && (
                  <div className="mb-2 px-2 py-1" style={{ fontSize: '0.78rem', color: '#8a6d00', background: '#fff8e1', border: '1px solid #ffe08a', borderRadius: 6 }}>
                    <i className="bi bi-exclamation-triangle me-1"></i>Classe grande — mostrando até 150 produtos por classe. Pra achar um item específico, use o campo <b>Descrição</b> (ex: "eliane 60x60") junto da classe.
                  </div>
                )}
                <div className="tabela-container" style={{ maxHeight: 340, minHeight: 120, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                  <table className="table table-mkt table-hover table-sm mb-0">
                    <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}><tr><th><input type="checkbox" onChange={e => setSelecionados(e.target.checked ? resultadosBusca.map(r => r.codpro) : [])} /></th><th>Código</th><th>Descrição</th><th>Classe</th><th>Disponib.</th><th style={{ textAlign: 'right' }}>Estoque</th><th>Custo</th><th>Venda</th><th>MKP</th></tr></thead>
                    <tbody>
                      {resultadosBusca.map(r => (
                        <tr key={r.codpro}><td><input type="checkbox" checked={selecionados.includes(r.codpro)} onChange={() => setSelecionados(prev => prev.includes(r.codpro) ? prev.filter(c => c !== r.codpro) : [...prev, r.codpro])} /></td><td>{r.codpro}</td><td title={r.descricao}>{descTela(r)}</td><td><small>{r.classe_nome}</small></td><td><small>{r.disponibilidade}</small></td><td style={{ textAlign: 'right', color: (r.estoque_disp || 0) > 0 ? '#1f7a3d' : '#b23b3b', fontWeight: 600 }}>{fmt(r.estoque_disp || 0)}</td><td>{fmt(r.custo)}</td><td>{fmt(r.preco_venda)}</td><td>{fmtMkp(r.mkp)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <small className="text-muted">{selecionados.length} selecionados</small>
                  <button className="btn-outline-mkt" onClick={adicionarSelecionadosNaLista} disabled={!selecionados.length}><i className="bi bi-arrow-down me-1"></i> Jogar selecionados na lista</button>
                </div>
              </>
            )}

            {/* Lista de staging — produtos a adicionar */}
            {paraAdicionar.length > 0 && (
              <div className="mt-3 pt-3" style={{ borderTop: '2px solid var(--mostarda-claro)' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold mb-0"><i className="bi bi-card-checklist me-1"></i>Produtos a adicionar ({paraAdicionar.length})</h6>
                  <button className="btn btn-sm btn-link text-muted p-0" onClick={() => setParaAdicionar([])}>limpar lista</button>
                </div>
                <div className="tabela-container" style={{ maxHeight: 240, overflowY: 'auto' }}>
                  <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.82rem' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                      <tr><th style={{ width: 40 }}>Foto</th><th>Código</th><th>Descrição</th><th>Classe</th><th>Custo</th><th>Venda</th><th></th></tr>
                    </thead>
                    <tbody>
                      {paraAdicionar.map(p => (
                        <tr key={p.codpro}>
                          <td><ImagemProduto codpro={p.codpro} tamanho={36} alt={p.descricao} /></td>
                          <td className="fw-semibold">{p.codpro}</td>
                          <td title={p.descricao}>{descTela(p)}</td>
                          <td><small>{p.classe_nome}</small></td>
                          <td>{fmt(p.custo)}</td>
                          <td>{fmt(p.preco_sistema)}</td>
                          <td><button className="btn btn-sm btn-outline-danger" onClick={() => removerDaLista(p.codpro?.trim())} title="Remover"><i className="bi bi-x"></i></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end align-items-center gap-2 mt-3 pt-2" style={{ borderTop: '1px solid #eee' }}>
              <button className="btn btn-outline-secondary" onClick={fecharBusca} disabled={salvandoLista}>Cancelar</button>
              <button className="btn-salvar" onClick={salvarLista} disabled={!paraAdicionar.length || salvandoLista}>
                {salvandoLista
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Salvando...</>
                  : <><i className="bi bi-check-lg me-1"></i>Salvar {paraAdicionar.length > 0 ? `${paraAdicionar.length} produto${paraAdicionar.length > 1 ? 's' : ''}` : ''} na campanha</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replicar preço nas outras listas (aviso de um produto ou "Alinhar com outras listas") */}
      {replicar && (
        <ReplicarPrecosModal modo={replicar.modo} itens={replicar.itens} replicando={replicando}
          onFechar={fecharAvisoReplicar} onConfirmar={confirmarReplicar} />
      )}

      {/* Modal Desconto em Lote */}
      {showLote && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card p-4" style={{ width: '90%', maxWidth: 800, maxHeight: '85vh', overflow: 'auto' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">Desconto em Lote</h5>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => { setShowLote(false); setPreviewLote([]); setTerminacaoLote(''); }}><i className="bi bi-x-lg"></i></button>
            </div>
            <div className="row g-2 mb-2">
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: '0.85rem' }}>Classe (opcional)</label>
                <select className="form-select" value={classeLote} onChange={e => setClasseLote(e.target.value)}>
                  <option value="">Todos os produtos</option>
                  {[...new Set(produtos.map(p => p.classe))].filter(Boolean).map(c => (<option key={c} value={c}>{produtos.find(p => p.classe === c)?.classe_nome || c}</option>))}
                </select>
              </div>
              <div className="col-md-3"><label className="form-label" style={{ fontSize: '0.85rem' }}>% Desconto sobre À Vista</label><input type="number" step="0.1" className="form-control" value={descontoPerc} onChange={e => setDescontoPerc(e.target.value)} placeholder="Ex: 18" /></div>
              <div className="col-md-2">
                <label className="form-label" style={{ fontSize: '0.85rem' }}>Terminação</label>
                <div className="input-group">
                  <span className="input-group-text" style={{ fontSize: '0.85rem' }}>,</span>
                  <input type="text" inputMode="numeric" className="form-control" value={terminacaoLote} onChange={e => setTerminacaoLote(e.target.value)} placeholder="90" />
                </div>
              </div>
              <div className="col-md-3 d-flex align-items-end"><button className="btn-outline-mkt w-100" onClick={simularLote} disabled={simulandoLote || !descontoPerc}>{simulandoLote ? <><span className="spinner-border spinner-border-sm me-1"></span>Simulando...</> : 'Simular'}</button></div>
            </div>
            <div className="mb-3" style={{ fontSize: '0.76rem', color: '#888' }}>
              <i className="bi bi-info-circle me-1"></i>
              <b>Terminação</b> arredonda o preço com desconto pra terminar nesses centavos (ex: <b>90</b> → ,90 · <b>99</b> → ,99). Arredonda pro mais próximo: até <i>X,49</i> desce, <i>X,50</i> pra cima sobe. Deixe vazio pra não arredondar.
            </div>
            {previewLote.length > 0 && (
              <>
                <div className="tabela-container" style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                  <table className="table table-mkt table-hover table-sm mb-0">
                    <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}><tr><th>Código</th><th>Descrição</th><th>À Vista Sist.</th><th>Novo Preço</th>{tem12x && <th>12x</th>}<th>MKP</th><th>Status</th></tr></thead>
                    <tbody>{previewLote.map(p => (<tr key={p.id}><td>{p.codpro}</td><td>{descTela(p)}</td><td>{fmt(p.preco_sistema)}</td><td className="fw-bold">{fmt(p.novo_preco)}</td>{tem12x && <td style={{ color: '#2F4F4F' }}>{p.novo_12x != null ? fmt(p.novo_12x) : '—'}</td>}<td>{fmtMkp(p.novo_mkp)}</td><td>{p.acima_sistema ? <span className="status-acima">ACIMA</span> : <span className="status-ok">OK</span>}</td></tr>))}</tbody>
                  </table>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <small className="text-muted"><i className="bi bi-exclamation-triangle me-1" style={{ color: '#c9962b' }}></i>Ao aplicar, <b>sobrescreve</b> o preço já configurado nesses {previewLote.length} produtos.</small>
                  <button className="btn-primary-mkt" onClick={aplicarLote} disabled={aplicandoLote}>{aplicandoLote ? <><span className="spinner-border spinner-border-sm me-1"></span>Aplicando...</> : <><i className="bi bi-check-lg me-1"></i> Aplicar em {previewLote.length} produtos</>}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Remover em Lote */}
      {showRemoverLote && (() => {
        const alvo = produtosParaRemover();
        const remover = alvo.filter(p => !remExcluidos.includes(p.id));
        const temDisp = remDisponibs.length > 0;
        const temClasse = remClasses.length > 0;
        const base = temDisp && remSemEstoque
          ? <>os <b>{remDisponibs.join(', ')}</b> <b>sem estoque na Central</b> (os com estoque ficam)</>
          : temDisp
            ? <>todos de <b>{remDisponibs.join(', ')}</b></>
            : remSemEstoque
              ? <>todos <b>sem estoque na Central</b></>
              : temClasse
                ? <>todos os produtos da(s) classe(s) marcada(s)</>
                : null;
        const criterio = base ? <>Vão sair {base}{temClasse && (temDisp || remSemEstoque) ? <>, só dentro de <b>{remClasses.join(', ')}</b></> : ''}.</> : null;
        const fechar = () => { setShowRemoverLote(false); setRemDisponibs([]); setRemClasses([]); setRemSemEstoque(false); setRemExcluidos([]); };
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="card p-4" style={{ width: '90%', maxWidth: 720, maxHeight: '88vh', overflow: 'auto' }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0"><i className="bi bi-trash me-2" style={{ color: '#b23b3b' }}></i>Remover em Lote</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={fechar}><i className="bi bi-x-lg"></i></button>
              </div>

              <div className="mb-2" style={{ fontSize: '0.85rem', color: '#555' }}>Marque as <b>disponibilidades</b> que quer tirar (ex: Encomenda). Dá pra <b>restringir a classes</b> e marcar <b>sem estoque na Central</b> — tudo combina em E (ex: <i>Fora de linha + Porcelanato + sem estoque</i>).</div>

              <label className="form-label fw-semibold" style={{ fontSize: '0.85rem' }}>Disponibilidades na campanha</label>
              <div className="d-flex flex-column gap-1 mb-2">
                {disponibsNaCampanha().map(([disp, n]) => (
                  <label key={disp} className="d-flex align-items-center gap-2 p-2 rounded" style={{ border: '1px solid #eee', cursor: 'pointer', fontSize: '0.88rem' }}>
                    <input type="checkbox" checked={remDisponibs.includes(disp)}
                      onChange={e => { setRemExcluidos([]); setRemDisponibs(prev => e.target.checked ? [...prev, disp] : prev.filter(d => d !== disp)); }} />
                    <span style={{ flex: 1 }}>{disp}</span>
                    <span className="badge" style={{ background: '#eee', color: '#555' }}>{n}</span>
                  </label>
                ))}
              </div>

              <label className="form-label fw-semibold" style={{ fontSize: '0.85rem' }}>Restringir a classes <span className="text-muted" style={{ fontWeight: 400 }}>(opcional — só tira dentro dessas classes)</span></label>
              <div className="d-flex flex-wrap gap-1 mb-3">
                {classesNaCampanha().map(([cls, n]) => {
                  const on = remClasses.includes(cls);
                  return (
                    <button key={cls} type="button"
                      onClick={() => { setRemExcluidos([]); setRemClasses(prev => on ? prev.filter(c => c !== cls) : [...prev, cls]); }}
                      className="btn btn-sm" style={{ fontSize: '0.8rem', border: `1px solid ${on ? '#6B2C5C' : '#ddd'}`, background: on ? 'var(--mostarda-claro)' : '#fff', color: on ? '#6B2C5C' : '#555', fontWeight: on ? 600 : 400 }}>
                      {on && <i className="bi bi-check2 me-1"></i>}{cls} <span style={{ opacity: 0.6 }}>({n})</span>
                    </button>
                  );
                })}
                {remClasses.length > 0 && <button type="button" className="btn btn-sm btn-link p-0 ms-1" style={{ fontSize: '0.78rem' }} onClick={() => { setRemExcluidos([]); setRemClasses([]); }}>limpar</button>}
              </div>

              <label className="d-flex align-items-center gap-2 p-2 rounded mb-3" style={{ border: '1px solid #eee', cursor: 'pointer', fontSize: '0.88rem' }}>
                <input type="checkbox" checked={remSemEstoque} onChange={e => { setRemExcluidos([]); setRemSemEstoque(e.target.checked); }} />
                <span>{temDisp ? <>Só os <b>sem estoque na Central</b> dentre os marcados (Central = 0)</> : <>Só os <b>sem estoque na Central</b> (Central = 0)</>}</span>
              </label>

              {alvo.length === 0 ? (
                <div className="p-2 rounded mb-3" style={{ background: '#f6f6f6', border: '1px solid #e5e5e5', fontSize: '0.88rem' }}>
                  Nenhum produto selecionado ainda — marque um critério acima.
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-1">
                    <label className="form-label fw-semibold mb-0" style={{ fontSize: '0.85rem' }}>
                      Vão sair <span style={{ color: '#b23b3b' }}>{remover.length}</span> de {alvo.length}
                      {remExcluidos.length > 0 && <span className="text-muted" style={{ fontWeight: 400 }}> · {remExcluidos.length} mantido(s)</span>}
                    </label>
                    {remExcluidos.length > 0 && <button className="btn btn-sm btn-link p-0" style={{ fontSize: '0.78rem' }} onClick={() => setRemExcluidos([])}>marcar todos de novo</button>}
                  </div>
                  {criterio && <div className="mb-1" style={{ fontSize: '0.78rem', color: '#7a3b3b' }}>{criterio}</div>}
                  <div className="tabela-container" style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.82rem' }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                        <tr><th style={{ width: 34 }} title="Marcado = remove; desmarcado = mantém"><i className="bi bi-trash" style={{ color: '#b23b3b' }}></i></th><th>Código</th><th>Descrição</th><th>Disponib.</th><th style={{ textAlign: 'right' }}>Central</th></tr>
                      </thead>
                      <tbody>
                        {alvo.map(p => {
                          const manter = remExcluidos.includes(p.id);
                          const central = getEstoque(p.codpro)?.estoque_central || 0;
                          return (
                            <tr key={p.id} style={{ opacity: manter ? 0.5 : 1 }}>
                              <td><input type="checkbox" checked={!manter} title={manter ? 'Manter na campanha' : 'Vai ser removido'} onChange={e => setRemExcluidos(prev => e.target.checked ? prev.filter(x => x !== p.id) : [...prev, p.id])} /></td>
                              <td className="fw-semibold" style={{ textDecoration: manter ? 'line-through' : 'none' }}>{p.codpro}</td>
                              <td style={{ textDecoration: manter ? 'line-through' : 'none' }}>{descTela(p)}</td>
                              <td><small>{p.disponibilidade}</small></td>
                              <td style={{ textAlign: 'right', color: central > 0 ? '#1f7a3d' : '#b23b3b', fontWeight: 600 }}>{fmt(central)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.76rem' }}>Desmarque um produto pra <b>mantê-lo</b> na campanha.</small>
                </>
              )}

              <div className="d-flex justify-content-end gap-2 mt-3">
                <button className="btn btn-outline-secondary" onClick={fechar}>Cancelar</button>
                <button className="btn btn-danger" onClick={removerLote} disabled={!remover.length || removendoLote}>
                  {removendoLote ? <><span className="spinner-border spinner-border-sm me-1"></span>Removendo...</> : <><i className="bi bi-trash me-1"></i>Remover {remover.length || ''}</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal Salvando */}
      {salvando && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card p-4 text-center" style={{ minWidth: 280 }}>
            <div className="spinner-border mb-3 mx-auto" style={{ color: '#6B2C5C', width: 40, height: 40 }} />
            <h6 className="fw-bold mb-1">Salvando alterações</h6>
            <p style={{ fontSize: '0.8rem', color: '#888', margin: 0 }}>Atualizando {produtos.length} produtos...</p>
          </div>
        </div>
      )}

      {/* Modal Loading Plaquinhas */}
      {gerandoPlaquinhas && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card p-4 text-center" style={{ minWidth: 320 }}>
            <div className="spinner-border mb-3 mx-auto" style={{ color: '#6B2C5C', width: 48, height: 48 }} />
            <h5 className="fw-bold mb-2">Gerando Plaquinhas</h5>
            <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>Preparando <strong>{qtdPlaquinhas}</strong> plaquinha{qtdPlaquinhas !== 1 ? 's' : ''} em PDF...</p>
            <p style={{ fontSize: '0.8rem', color: '#999', marginTop: 8 }}>Isso pode levar alguns segundos</p>
          </div>
        </div>
      )}
    </div>
  );
}
